import { ConfigService, DashboardRelationshipOption } from 'app/config/config.service';
import {
    UserGeneratedConfigService,
    ImsUserConfig,
} from 'app/config/user_generated_config.service';
import { AbstractRestrictionsService } from 'app/licensing/restriction_service_interface';
import {
    isIPDevicesEnabled,
    isTriggersEnabled,
    isZonesEnabled,
} from 'app/licensing/restriction_utils';
import {
    autoTimeSliceSize,
    customTimeSliceSizeOptionsForConfig,
    validTimeSliceSizes,
} from 'app/overview/dashboard/config/time_slice';
import { getAggregationSuffix } from 'app/overview/dashboard/dashboard_data_transformers';
import {
    DashboardItemStateProvider,
    LegendState,
} from 'app/overview/dashboard/DashboardItemStateProvider';
import { IMS_TYPE__ASSET } from 'app/shared/collections/asset';
import { IMS_TYPE__ASSET_TYPE } from 'app/shared/collections/asset_type';
import { DynamicStatSchemaCollection } from 'app/shared/collections/dynamic_stat_schema_collection.service';
import { IMS_TYPE__IP_DEVICE } from 'app/shared/collections/ip_device';
import { IMS_TYPE__IP_DEVICE_TYPE } from 'app/shared/collections/ip_device_type';
import { IMS_TYPE__RADIO } from 'app/shared/collections/radio';
import {
    FIELD_SCHEMAS__TYPE_BOOLEAN,
    FIELD_SCHEMAS__TYPE_FILE,
    FIELD_SCHEMAS__TYPE_IMAGE,
    FIELD_SCHEMAS__TYPE_IP_DEVICE,
    FIELD_SCHEMAS__TYPE_LON_LAT,
    FIELD_SCHEMAS__TYPE_POINT,
    FIELD_SCHEMAS__TYPE_POLYGON,
    FIELD_SCHEMAS__TYPE_RADIO,
    FIELD_SCHEMAS__TYPE_STRING,
    FIELD_SCHEMAS__TYPE_TENLOGTEN,
    FIELD_SCHEMAS__TYPE_ZONE,
    FieldSchema,
    FieldSchemaUnits,
    ForeignKeyFieldType,
    isForeignKeyField,
    STAT_DEFINITION__PARENT_TYPE_ASSET,
    STAT_DEFINITION__PARENT_TYPE_IP_DEVICE,
    STAT_DEFINITION__PARENT_TYPE_RADIO,
    STAT_DEFINITION__PARENT_TYPE_ZONE,
    StatDefinition,
} from 'app/shared/collections/StatDefinition';
import { IMS_TYPE__ASSET_TAG } from 'app/shared/collections/tags/asset_tag_collection.service';
import { IMS_TYPE__IP_DEVICE_TAG } from 'app/shared/collections/tags/ip_device_tag_collection.service';
import { IMS_TYPE__TRIGGER } from 'app/shared/collections/trigger';
import { IMS_TYPE__ZONE } from 'app/shared/collections/zones/zone';
import { IMS_TYPE__ZONE_TYPE } from 'app/shared/collections/zones/zone_type';
import { IMS_TYPE__RADIO_TECHNOLOGY } from 'app/shared/collections/radio_technology';
import { ColorRendererService } from 'app/shared/color_renderer.service';
import DebugService from 'app/shared/debug.service';
import { CachedEventRegistry } from 'app/shared/events/cached_event_registry';
import { EventRegistry } from 'app/shared/events/event_registry';
import FilterService from 'app/shared/filter.service';
import { ImsItemTypeDetails, ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { createDynamicStatAggResources } from 'app/shared/resources';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { TimeZone } from 'app/shared/time/TimeZone';
import { UnitService } from 'app/shared/unit.service';
import Constant from 'app/shared/utilities/constant';
import { extractPage } from 'app/shared/utilities/pagination';
import { TimeUnit, toSeconds } from 'app/shared/utilities/time';
import { Dictionary } from 'app/types';
import I from 'immutable';
import * as _ from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { Operations } from './DashboardItemPostAggregationOptions';
import { PAGE_SIZE_ALL } from 'app/overview/dashboard/dashboard_item_legend_panel/dashboard_item_legend.component';
import { IMS_TYPE__RADIO_TECHNOLOGY_TYPE } from 'app/shared/collections/radio_technology_type';

interface DynamicStatTableDataSets {
    fieldDetails: FieldDetailsDict;
    fieldTypeDetails: FieldTypeDetailsDict;
    dataSourceDetails: DataSourceDetailsDict;
    dataSources: unknown[];
}

const GROUP_BY_ALL = 'all';
const GROUP_BY_ASSET = 'asset';
const GROUP_BY_ASSET_TYPE = 'asset_type';
const GROUP_BY_ASSET_TAG = 'asset_tag';
const GROUP_BY_IP_DEVICE = 'ip_device';
const GROUP_BY_IP_DEVICE_TAG = 'ip_device_tag';
const GROUP_BY_IP_DEVICE_TYPE = 'ip_device_type';
const GROUP_BY_RADIO = 'radio';
const GROUP_BY_RADIO_TECHNOLOGY_CATEGORY = 'radio_technology';
const GROUP_BY_RADIO_TECHNOLOGY_TYPE = 'radio_technology_type';
const GROUP_BY_ZONE = 'zone';
const GROUP_BY_ZONE_TYPE = 'zone_type';
const GROUP_BY_WIFI_CHANNEL = 'wifi_channel';
const GROUP_BY_TRIGGER = 'trigger';
const FILTER_BY_ASSET_ID = FilterService.FILTER_BY_ASSET;
const FILTER_BY_ASSET_TYPE_ID = FilterService.FILTER_BY_ASSET_TYPE;
const FILTER_BY_ASSET_TAG_ID = FilterService.FILTER_BY_ASSET_TAG;
const FILTER_BY_IP_DEVICE_ID = FilterService.FILTER_BY_IP_DEVICE;
const FILTER_BY_IP_DEVICE_TYPE = FilterService.FILTER_BY_IP_DEVICE_TYPE;
const FILTER_BY_IP_DEVICE_TAG_ID = FilterService.FILTER_BY_IP_DEVICE_TAG;
const FILTER_BY_RADIO_ID = FilterService.FILTER_BY_RADIO;
const FILTER_BY_RADIO_TECHNOLOGY_CATEGORY = FilterService.FILTER_BY_RADIO_TECHNOLOGY_CATEGORY;
const FILTER_BY_RADIO_TECHNOLOGY_TYPE = FilterService.FILTER_BY_RADIO_TECHNOLOGY_TYPE;
const FILTER_BY_ZONE_ID = FilterService.FILTER_BY_ZONE;
const FILTER_BY_ZONE_TYPE_ID = FilterService.FILTER_BY_ZONE_TYPE;
const FILTER_BY_TRIGGER_ID = FilterService.FILTER_BY_TRIGGER;
const FILTER_BY_SUBGROUP_NAME = FilterService.FILTER_BY_SUBGROUP_NAME;
const FILTER_BY_WIFI_CHANNEL = FilterService.FILTER_BY_WIFI_CHANNEL;

//Filter/Group by WIFI Channel can only be done on timeslices < 300s
const WIFI_CHANNEL_TIMESLICE_THRESHOLD = 300;

const MAX_TREND_DATASET_SIZE = 50;

/**
 * How many stats per line chart do we estimate before queries start getting slow.
 * Eg, if you're going to request about 5000 stats per asset be aggregated from the database
 *  then we can warn the user it might be slow.
 */
const MAX_RECOMMENDED_STATS_PER_GRAPH = 5000;

// Upper limit of five minute aggregate time in seconds.
const FIVE_MINUTE_SEPERATION = 1000000;

/**
 * Given a stat frequency and a max number of stats, calculate the time frame which we'll hit that limit.
 */
function calculateMaxRecommendedTimeFrameInSeconds(
    estimatedStatFrequency: number,
    maxRecommendedStats: number,
): number {
    return estimatedStatFrequency * maxRecommendedStats;
}

function createResourceDefinitionWithHourlyAnd5mRollup(
    resourceName: string,
    frequency: number,
    maxTimeFrameSeconds: number,
    fiveMinuteResourceName: string,
    fiveMinuteMaxTimeFrameInSeconds: number,
    hourlyResourceName: string,
    hourlyMaxTimeFrameInSeconds: number,
): DataSourceResources {
    const dataSourceResources = createResourceDefinitionWithHourlyRollup(
        resourceName,
        frequency,
        maxTimeFrameSeconds,
        hourlyResourceName,
        hourlyMaxTimeFrameInSeconds,
    );

    dataSourceResources.preAggregated.push({
        name: fiveMinuteResourceName,
        aggSeconds: toSeconds(5, TimeUnit.MINUTES) - 1,
        recommendedMaxTimeFrameSeconds: calculateMaxRecommendedTimeFrameInSeconds(
            toSeconds(5, TimeUnit.MINUTES),
            MAX_RECOMMENDED_STATS_PER_GRAPH,
        ),
        maxTimeFrameSeconds: fiveMinuteMaxTimeFrameInSeconds,
    });

    return dataSourceResources;
}

/**
 * Create the basic resources definition for a table with a single hourly rollup.
 * @param resourceName The name of the non-rollup default endpoint
 * @param frequency The frequency of that non-rolled up stats
 * @param maxTimeFrameSeconds The max time frame for the non-hourly resource
 * @param hourlyResourceName The name of hourly aggregated endpoint
 * @param hourlyMaxTimeFrameInSeconds The max time frame for the hourly resource
 */
function createResourceDefinitionWithHourlyRollup(
    resourceName: string,
    frequency: number,
    maxTimeFrameSeconds: number,
    hourlyResourceName: string,
    hourlyMaxTimeFrameInSeconds: number,
): DataSourceResources {
    return {
        default: resourceName,
        // say at 8 seconds, 5000 stats would give us ~11 hours, so if you request over that time using this resource, you'll get a warning.
        defaultRecommendedMaxTimeFrameSeconds: calculateMaxRecommendedTimeFrameInSeconds(
            frequency,
            MAX_RECOMMENDED_STATS_PER_GRAPH,
        ),
        defaultMaxTimeFrameSeconds: maxTimeFrameSeconds,
        preAggregated: [
            {
                name: hourlyResourceName,
                aggSeconds: toSeconds(1, TimeUnit.HOURS),
                // at 1 hour roll ups, 5000 stats would give us about ~6 months before a warning appears
                recommendedMaxTimeFrameSeconds: calculateMaxRecommendedTimeFrameInSeconds(
                    toSeconds(1, TimeUnit.HOURS),
                    MAX_RECOMMENDED_STATS_PER_GRAPH,
                ),
                maxTimeFrameSeconds: hourlyMaxTimeFrameInSeconds,
            },
        ],
    };
}

export const DATA_SOURCE_TRIGGER = 'trigger';
const FIELD_TRIGGER_TRIPPED_PERCENTAGE = 'trigger_tripped_percentage';

const log = (...args) => DebugService.log('DashboardItemService', ...args);

const LEGEND_STATE = 'legendState';
const VIS_CONFIG = 'visConfig';

export const ITEM_TYPE_CHART = 'chart';
export const ITEM_TYPE_TABLE = 'table';
export const ITEM_TYPE_STATUS = 'status';

const ITEM_TYPES = I.List.of(ITEM_TYPE_CHART, ITEM_TYPE_TABLE, ITEM_TYPE_STATUS);

const ITEM_TYPE_DETAILS = I.fromJS({
    chart: {
        name: 'Chart',
        validRelativeTimeFrames: [5, 15, 30, 60, 180, 360, 720, 1440, 10080, 40320, 120960, 262800],
    },
    table: {
        name: 'Table',
        validRelativeTimeFrames: [
            1, 5, 15, 30, 60, 180, 360, 720, 1440, 10080, 40320, 120960, 262800,
        ],
    },
    status: {
        name: 'Status',
        validRelativeTimeFrames: [
            1, 5, 15, 30, 60, 180, 360, 720, 1440, 10080, 40320, 120960, 262800,
        ],
    },
});

export const CHART_DISPLAY_TYPE_LINE = 'line';
export const CHART_DISPLAY_TYPE_BAR = 'bar';
export const CHART_DISPLAY_TYPE_ACCUMULATED_BAR = 'accumulated_bar';

// Chart types that have pagination enabled
const PAGINATION_CHARTS = [CHART_DISPLAY_TYPE_LINE, CHART_DISPLAY_TYPE_BAR];

const CHART_DISPLAY_TYPES = I.List.of(
    CHART_DISPLAY_TYPE_LINE,
    CHART_DISPLAY_TYPE_BAR,
    CHART_DISPLAY_TYPE_ACCUMULATED_BAR,
);

interface FieldTypeDetails {
    name: string;
    chartYMax: number;
    chartYMin: number;
}

function filterAllowableIPDeviceStatValidRelativeTimeFrames(value: number): boolean {
    return value >= (ConfigService.ipDeviceStatDataFrequency / 60.0) * 2;
}

const IP_DEVICE_STAT_MIN_TIME_SLICE_SIZE = ConfigService.ipDeviceStatDataFrequency;

type FieldTypeDetailsDict = Dictionary<FieldTypeDetails>;

type AggregationType =
    | 'avg'
    | 'sum'
    | 'count'
    | 'max'
    | 'min'
    | 'cumulative_sum'
    | 'percentage'
    | 'log_sum'
    | 'log_avg';

let fieldTypeDetails: FieldTypeDetailsDict = {
    signal: {
        name: 'RSSI & Noise',
        chartYMax: -40,
        chartYMin: -110,
    },
    snr: {
        name: 'SNR',
        chartYMax: 70,
        chartYMin: 0,
    },
    rsrp: {
        name: 'RSRP',
        chartYMax: -40,
        chartYMin: -130,
    },
    rsrq: {
        name: 'RSRQ',
        chartYMax: 0,
        chartYMin: -20,
    },
    sinr: {
        name: 'SINR',
        chartYMax: 30,
        chartYMin: 0,
    },
    channel_utilisation: {
        name: 'Channel Utilisation',
        chartYMax: 100,
        chartYMin: 0,
    },
    utilisation: {
        name: 'Utilisation',
        chartYMax: 1,
        chartYMin: 0,
    },
    total_utilisation: {
        name: 'Total Utilisation',
        chartYMax: 1,
        chartYMin: 0,
    },
    rate: {
        name: 'Rate',
        chartYMax: 1,
        chartYMin: 0,
    },
    latency: {
        name: 'Latency',
        chartYMax: 1,
        chartYMin: 0,
    },
    connectivity: {
        name: 'Connectivity',
        chartYMax: 100,
        chartYMin: 0,
    },
    battery_temp: {
        name: 'Battery Temperature',
        chartYMax: 1,
        chartYMin: 0,
    },
    /* Power related to solar controllers */
    power: {
        name: 'Power',
        chartYMax: 1,
        chartYMin: 0,
    },
    /* Specifically transmitter power for radios; NOTE: not necessarily sensible to chart! */
    tx_power: {
        name: 'Tx Power',
        chartYMax: 1,
        chartYMin: 0,
    },
    /* Specifically channel for radios; NOTE: not necessarily sensible to chart! */
    channel: {
        name: 'Channel',
        chartYMax: 1,
        chartYMin: 0,
    },
    // Note: Type here is just 'voltage' instead of 'battery_voltage' for backward compatibility.
    // panel_voltage and battery_voltage used to be combined to a single chart.
    voltage: {
        name: 'Battery Voltage',
        chartYMax: 1,
        chartYMin: 0,
    },
    panel_voltage: {
        name: 'Panel Voltage',
        chartYMax: 1,
        chartYMin: 0,
    },
    current: {
        name: 'Current',
        chartYMax: 1,
        chartYMin: 0,
    },
    total_cpu_percentage: {
        name: 'Total CPU % Used',
        chartYMax: 100,
        chartYMin: 0,
    },
    total_ram_percentage: {
        name: 'Total RAM % Used',
        chartYMax: 100,
        chartYMin: 0,
    },
    total_storage_percentage: {
        name: 'Total Storage % Used',
        chartYMax: 100,
        chartYMin: 0,
    },
    load_1_min: {
        name: '1m CPU Load',
        chartYMax: 1,
        chartYMin: 0,
    },
    load_5_min: {
        name: '5m CPU Load',
        chartYMax: 1,
        chartYMin: 0,
    },
    load_15_min: {
        name: '15m CPU Load',
        chartYMax: 1,
        chartYMin: 0,
    },
    normalized_load_1_min: {
        name: '1m Normalized CPU Load',
        chartYMax: 1,
        chartYMin: 0,
    },
    normalized_load_5_min: {
        name: '5m Normalized CPU Load',
        chartYMax: 1,
        chartYMin: 0,
    },
    normalized_load_15_min: {
        name: '15m Normalized CPU Load',
        chartYMax: 1,
        chartYMin: 0,
    },
    ram_used_percentage: {
        name: 'RAM % Used by Process',
        chartYMax: 100,
        chartYMin: 0,
    },
    ram_used: {
        name: 'RAM Used by Process',
        chartYMax: 1,
        chartYMin: 0,
    },
    cpu_percentage: {
        name: 'CPU Used by Process',
        chartYMax: 100,
        chartYMin: 0,
    },
    storage_total: {
        name: 'Storage Total by Volume',
        chartYMax: 1,
        chartYMin: 0,
    },
    storage_used_percentage: {
        name: 'Storage % Used by Volume',
        chartYMax: 100,
        chartYMin: 0,
    },
    unique_roams: {
        name: 'Unique Roams',
        chartYMax: 10,
        chartYMin: 0,
    },
    unique_clients: {
        name: 'Unique Clients',
        chartYMax: 10,
        chartYMin: 0,
    },
    client_connection_time: {
        name: 'Time Connected as Client',
        chartYMax: 1,
        chartYMin: 0,
    },
    ap_connection_time: {
        name: 'Time Connected as AP',
        chartYMax: 1,
        chartYMin: 0,
    },
    distance_as_ap: {
        name: 'Connection Distance as AP',
        chartYMax: 1,
        chartYMin: 0,
    },
    distance_as_client: {
        name: 'Connection Distance as Client',
        chartYMax: 1,
        chartYMin: 0,
    },
    [FIELD_TRIGGER_TRIPPED_PERCENTAGE]: {
        name: 'Tripped',
        chartYMax: 100,
        chartYMin: 0,
    },
};

fieldTypeDetails = Object.assign(
    fieldTypeDetails,
    ...ConfigService.packetConfigs.map(({ humanName, logicalName }) => ({
        [logicalName]: {
            name: humanName,
            chartYMax: 10,
            chartYMin: 0,
        } as FieldTypeDetails,
    })),
);

let FIELD_TYPE_DETAILS = I.fromJS(fieldTypeDetails);

const aggregationTypeOptions = {
    avg: {
        name: 'Average',
        suffix: ' Avg',
        value: 'avg',
    },
    sum: {
        name: 'Sum',
        suffix: ' Sum',
        value: 'sum',
    },
    max: {
        name: 'Max',
        suffix: ' Max',
        value: 'max',
    },
    min: {
        name: 'Min',
        suffix: ' Min',
        value: 'min',
    },
    count: {
        name: 'Count',
        suffix: ' Count',
        value: 'count',
    },
    cumulative_sum: {
        name: 'Cumulative Sum',
        suffix: ' Cum. Sum',
        value: 'cumulative_sum',
    },
    log_sum: {
        name: 'Log Sum',
        suffix: ' Log Sum',
        value: 'log_sum',
    },
    log_avg: {
        name: 'Log Average',
        suffix: ' Log Avg',
        value: 'log_avg',
    },
    percentage: {
        name: 'Percentage',
        suffix: '  %',
        value: 'percentage',
    },
};

const AGGREGATION_TYPE_OPTIONS = I.fromJS(aggregationTypeOptions);

interface FieldDetails {
    name: string;
    value: string;
    type: string | null;
    unit: FieldSchemaUnits;
    multiplier?: number;
    aggregationTypes: Array<AggregationType>;
    aggregationTypesSuffix: Array<string>;
}

type FieldDetailsDict = Dictionary<FieldDetails>;

function getUtilisationFieldDetails(
    name: string,
    value: string,
    throughputAsBits: boolean,
): FieldDetails {
    return {
        name: name,
        // when throughputAsBits is false, we need to convert it to bytes
        multiplier: throughputAsBits ? 1 : 1 / 8,
        type: 'utilisation',
        value: value,
        unit: throughputAsBits ? Constant.UNITS.BITRATE : Constant.UNITS.BYTERATE,
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: [],
    };
}

function getTotalUtilisationFieldDetails(
    name: string,
    value: string,
    throughputAsBits: boolean,
): FieldDetails {
    return {
        name: name,
        // when throughputAsBits is false, we need to convert it to bytes
        multiplier: throughputAsBits ? 1 : 1 / 8,
        type: 'total_utilisation',
        value: value,
        unit: throughputAsBits ? Constant.UNITS.BITS : Constant.UNITS.BYTES,
        aggregationTypes: ['sum'],
        aggregationTypesSuffix: [],
    };
}

let fieldDetails: FieldDetailsDict = {};

let FIELD_DETAILS = I.fromJS(fieldDetails);

// This function is called after the definition of fieldDetails and any time the UserGeneratedConfigService updates
function updateFieldDetailsWithUtilisation(throughputAsBits: boolean): void {
    const newFieldDetails = {
        avg_tx_bps: getUtilisationFieldDetails('Tx Utilisation', 'avg_tx_bps', throughputAsBits),
        avg_rx_bps: getUtilisationFieldDetails('Rx Utilisation', 'avg_rx_bps', throughputAsBits),
        cumulative_total_tx_bps: getTotalUtilisationFieldDetails(
            'Total Tx Utilisation',
            'cumulative_total_tx_bps',
            throughputAsBits,
        ),
        cumulative_total_rx_bps: getTotalUtilisationFieldDetails(
            'Total Rx Utilisation',
            'cumulative_total_rx_bps',
            throughputAsBits,
        ),
    };

    // use FIELD_DETAILS as a base to handle any changes loadDynamicStatTableDataSets made
    FIELD_DETAILS = I.fromJS(Object.assign(FIELD_DETAILS.toObject(), newFieldDetails));

    // reset fieldDetails to ensure loadDynamicStatTableDataSets considers these changes (if it fires again)
    fieldDetails = FIELD_DETAILS.toJS();
}

// Keyed by either field name or table.name (eg panel_voltage, or solar_controller_stat.panel_voltage)
// The 'value' is the name of the api field itself
fieldDetails = {
    ip_device_log_ip_device_name: {
        name: 'IP Device',
        value: 'ip_device_log_ip_device_name',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    ip_device_log_file_name: {
        name: 'Log File Name',
        value: 'ip_device_log_file_name',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    ip_device_log_level: {
        name: 'Log Level',
        value: 'ip_device_log_level',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    ip_device_log_text: {
        name: 'Log Text',
        value: 'ip_device_log_text',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    ip_device_log_service_name: {
        name: 'Service Name',
        value: 'ip_device_log_service_name',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    ip_device_log_timestamp: {
        name: 'Timestamp',
        value: 'ip_device_log_timestamp',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    // device fields
    monitor_ip: {
        name: 'Monitor IP',
        value: 'monitor_ip',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    mac: {
        name: 'MAC',
        value: 'mac',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    note: {
        name: 'Note',
        value: 'note',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    type: {
        name: 'Type',
        value: 'type',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    ignore_stats: {
        name: 'Ignore Stats',
        value: 'ignore_stats',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },
    tags: {
        name: 'Tags',
        value: 'tags',
        type: null,
        unit: {
            suffix: '',
        },
        aggregationTypes: [],
        aggregationTypesSuffix: [],
    },

    // stat fields
    noise: {
        name: 'Noise',
        type: 'signal',
        value: 'noise',
        unit: {
            suffix: 'dBm',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['dBm'],
    },
    avg_rssi: {
        name: 'RSSI',
        type: 'signal',
        value: 'avg_rssi',
        unit: {
            suffix: 'dBm',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['dBm'],
    },
    snr: {
        name: 'SNR',
        type: 'snr',
        value: 'snr',
        unit: {
            suffix: 'dB',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['dB'],
    },
    avg_rsrp: {
        name: 'RSRP',
        type: 'rsrp',
        value: 'avg_rsrp',
        unit: {
            suffix: 'dBm',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['dBm'],
    },
    avg_rsrq: {
        name: 'RSRQ',
        type: 'rsrq',
        value: 'avg_rsrq',
        unit: {
            suffix: 'dB',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['dB'],
    },
    avg_sinr: {
        name: 'SINR',
        type: 'sinr',
        value: 'avg_sinr',
        unit: {
            suffix: 'dB',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['dB'],
    },
    channel_utilisation: {
        name: 'Channel Utilisation',
        type: 'channel_utilisation',
        value: 'channel_utilisation',
        unit: {
            suffix: Constant.Measurement.CHANNEL_UTILISATION_UNIT,
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: [Constant.Measurement.CHANNEL_UTILISATION_UNIT],
    },
    // NOTE: avg_tx_bps, avg_rx_bps, cumulative_total_tx_bps and cumulative_total_rx_bps are set by
    // updateFieldDetailsWithUtilisation below this dict
    avg_tx_rate: {
        name: 'Tx Rate',
        type: 'rate',
        value: 'avg_tx_rate',
        unit: {
            suffix: 'Mbit/s',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['Mbit/s'],
    },
    avg_rx_rate: {
        name: 'Rx Rate',
        type: 'rate',
        value: 'avg_rx_rate',
        unit: {
            suffix: 'Mbit/s',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['Mbit/s'],
    },
    tx_power: {
        name: 'Tx Power',
        type: 'tx_power',
        value: 'tx_power',
        unit: {
            suffix: 'dBm',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['dBm'],
    },
    channel: {
        name: 'Channel',
        type: 'channel',
        value: 'channel',
        unit: {
            suffix: Constant.Measurement.CHANNEL_UNIT,
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: [Constant.Measurement.CHANNEL_UNIT],
    },
    latency: {
        name: 'Latency',
        type: 'latency',
        value: 'latency',
        unit: {
            suffix: 'ms',
        },
        aggregationTypes: ['avg', 'count', 'min', 'max', 'sum'],
        aggregationTypesSuffix: ['ms'],
    },
    connectivity: {
        name: 'Connectivity',
        type: 'connectivity',
        value: 'connectivity',
        unit: {
            suffix: '%',
        },
        aggregationTypes: ['avg', 'count', 'min', 'max', 'sum'],
        aggregationTypesSuffix: ['%'],
    },
    battery_temp: {
        name: 'Battery Temperature',
        type: 'battery_temp',
        value: 'battery_temp',
        unit: {
            suffix: '°C',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['°C'],
    },
    output_power: {
        name: 'Output Power',
        type: 'power',
        value: 'output_power',
        unit: {
            suffix: 'W',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['W'],
    },
    input_power: {
        name: 'Input Power',
        type: 'power',
        value: 'input_power',
        unit: {
            suffix: 'W',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['W'],
    },
    panel_voltage: {
        name: 'Panel Voltage',
        type: 'panel_voltage',
        value: 'panel_voltage',
        unit: {
            suffix: 'V',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['V'],
    },
    // Note: Type here is just 'voltage' instead of 'battery_voltage' for backward compatibility.
    // panel_voltage and battery_voltage used to be combined to a single chart.
    battery_voltage: {
        name: 'Battery Voltage',
        type: 'voltage',
        value: 'battery_voltage',
        unit: {
            suffix: 'V',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['V'],
    },
    panel_current: {
        name: 'Panel Current',
        type: 'current',
        value: 'panel_current',
        unit: {
            suffix: 'A',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['A'],
    },
    battery_current: {
        name: 'Battery Current',
        type: 'current',
        value: 'battery_current',
        unit: {
            suffix: 'A',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['A'],
    },
    total_cpu_percentage: {
        name: 'Total CPU % Used',
        type: 'total_cpu_percentage',
        value: 'total_cpu_percentage',
        unit: {
            suffix: '%',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['%'],
    },
    total_ram_percentage: {
        name: 'Total RAM % Used',
        type: 'total_ram_percentage',
        value: 'total_ram_percentage',
        unit: {
            suffix: '%',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['%'],
    },
    total_storage_percentage: {
        name: 'Total Storage % Used',
        type: 'total_storage_percentage',
        value: 'total_storage_percentage',
        unit: {
            suffix: '%',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['%'],
    },
    load_1_min: {
        name: '1m CPU Load',
        type: 'load_1_min',
        value: 'load_1_min',
        unit: {
            suffix: '',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: [''],
    },
    load_5_min: {
        name: '5m CPU Load',
        type: 'load_5_min',
        value: 'load_5_min',
        unit: {
            suffix: '',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: [''],
    },
    load_15_min: {
        name: '15m CPU Load',
        type: 'load_15_min',
        value: 'load_15_min',
        unit: {
            suffix: '',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: [''],
    },
    normalized_load_1_min: {
        name: '1m Normalized CPU Load',
        type: 'normalized_load_1_min',
        value: 'normalized_load_1_min',
        unit: {
            suffix: '',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: [''],
    },
    normalized_load_5_min: {
        name: '5m Normalized CPU load',
        type: 'normalized_load_5_min',
        value: 'normalized_load_5_min',
        unit: {
            suffix: '',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: [''],
    },
    normalized_load_15_min: {
        name: '15m Normalized CPU load',
        type: 'normalized_load_15_min',
        value: 'normalized_load_15_min',
        unit: {
            suffix: '',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: [''],
    },
    ram_used: {
        name: 'RAM Used by Process',
        type: 'ram_used',
        multiplier: 1 / 1048576, // bytes -> mb
        value: 'ram_used',
        unit: {
            suffix: 'MB',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['MB'],
    },
    ram_used_percentage: {
        name: 'RAM % Used by Process',
        type: 'ram_used_percentage',
        value: 'ram_used_percentage',
        unit: {
            suffix: '%',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['%'],
    },
    cpu_percentage: {
        name: 'CPU Used by Process',
        type: 'cpu_percentage',
        value: 'cpu_percentage',
        unit: {
            suffix: '%',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['%'],
    },
    storage_used_percentage: {
        name: 'Storage % Used by Volume',
        type: 'storage_used_percentage',
        value: 'storage_used_percentage',
        unit: {
            suffix: '%',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['%'],
    },
    storage_total: {
        name: 'Storage Total by Volume',
        type: 'storage_total',
        multiplier: 1 / 1073741824, // bytes -> gb
        value: 'storage_total',
        unit: {
            suffix: 'GB',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['GB'],
    },
    client_connection_time: {
        name: 'Time Connected as Client',
        type: 'client_connection_time',
        multiplier: 1 / 60, // sec -> min
        value: 'client_connection_time',
        unit: {
            suffix: 'm',
        },
        aggregationTypes: ['sum'],
        aggregationTypesSuffix: ['m'],
    },
    ap_connection_time: {
        name: 'Time Connected as AP',
        type: 'ap_connection_time',
        multiplier: 1 / 60, // sec -> min
        value: 'ap_connection_time',
        unit: {
            suffix: 'm',
        },
        aggregationTypes: ['sum'],
        aggregationTypesSuffix: ['m'],
    },
    unique_clients: {
        name: 'Unique Clients',
        type: 'unique_clients',
        value: 'unique_clients',
        unit: {
            suffix: '',
        },
        aggregationTypes: ['sum'],
        aggregationTypesSuffix: [''],
    },
    unique_roams: {
        name: 'Unique Roams',
        type: 'unique_roams',
        value: 'unique_roams',
        unit: {
            suffix: '',
        },
        aggregationTypes: ['sum'],
        aggregationTypesSuffix: [''],
    },
    distance_as_ap: {
        name: 'Distance',
        type: 'distance_as_ap',
        value: 'distance_as_ap',
        unit: {
            suffix: 'm',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['m'],
    },
    distance_as_client: {
        name: 'Distance',
        type: 'distance_as_client',
        value: 'distance_as_client',
        unit: {
            suffix: 'm',
        },
        aggregationTypes: ['avg'],
        aggregationTypesSuffix: ['m'],
    },
    [FIELD_TRIGGER_TRIPPED_PERCENTAGE]: {
        name: 'Tripped',
        type: FIELD_TRIGGER_TRIPPED_PERCENTAGE,
        value: 'tripped_percentage',
        unit: {
            suffix: '%',
        },
        aggregationTypes: ['percentage'],
        aggregationTypesSuffix: ['%'],
    },
};

fieldDetails = Object.assign(
    fieldDetails,
    ...ConfigService.packetConfigs.map(({ humanName, logicalName }) => ({
        [logicalName]: {
            name: humanName,
            type: logicalName,
            value: logicalName,
            unit: {
                suffix: '',
            },
            aggregationTypes: ['avg'],
            aggregationTypesSuffix: [''],
        } as FieldDetails,
    })),
);

FIELD_DETAILS = I.fromJS(fieldDetails);

updateFieldDetailsWithUtilisation(true);

const LABEL_DIRECTION_OPTIONS = I.List.of('horizontal', 'vertical');
const LABEL_DIRECTION_NAME_OPTIONS = I.Map({
    horizontal: 'Horizontal',
    vertical: 'Vertical',
});

const IGNORED_STAT_OPTIONS = I.List.of('hide', 'show', 'all');
const IGNORED_STAT_NAME_OPTIONS = I.Map({
    hide: 'Hide Ignored Stats',
    show: 'Show Only Ignored Stats',
    all: 'Show All Stats',
});

const IS_ACTIVE_OPTIONS = I.List.of('active', 'inactive', 'all');
const IS_ACTIVE_NAME_OPTIONS = I.Map({
    active: 'Show Only Uplink Radios',
    inactive: 'Show Only Non-Uplink Radios',
    all: 'Show All Radios',
});

const AUTO_REFRESH_RATE_SECONDS_OPTIONS = I.List.of(10, 20, 30, 60, 120, 300);
const AUTO_REFRESH_RATE_SECONDS_NAME_OPTIONS = I.Map({
    10: '10 seconds',
    20: '20 seconds',
    30: '30 seconds',
    60: '1 minute',
    120: '2 minutes',
    300: '5 minutes',
});

let DATA_SOURCES = I.fromJS([
    'radio',
    'ip_device',
    'solar_controller_stat',
    'hardware',
    'process',
    'storage',
    'connections_as_client',
    'connections_as_ap',
    'connection_distance_as_client',
    'connection_distance_as_ap',
    DATA_SOURCE_TRIGGER,
]);

export interface DataSourceResource {
    // the name of the resource (endpoint)
    name: string;
    // the number of seconds this resource is aggregated over
    aggSeconds: number;
    // the max recommended time frame for this resource;
    //  any time above this will produce a warning of slow queries.
    recommendedMaxTimeFrameSeconds?: number;
    // the actual maximum time frame this resource can support.
    //  this resource won't be an option if the time frame is bigger than this value.
    maxTimeFrameSeconds?: number;
}

interface DataSourceResources {
    // the default resource name
    default: string;
    // the max recommended time frame for the default resource;
    //  any time above this will produce a warning of slow queries.
    defaultRecommendedMaxTimeFrameSeconds?: number;
    // the actual maximum time frame this resource can support.
    //  this resource won't be an option if the time frame is bigger than this value.
    defaultMaxTimeFrameSeconds?: number;
    preAggregated: Array<DataSourceResource>;
}

type FilterTypes = Array<string>;
type GroupTypes = Array<string>;

interface DataSourceDetails {
    name: string;
    fields: Array<string>;
    filterTypes: FilterTypes;
    groups: GroupTypes;
    defaultGroup: string;
    parentType?: string;
    hasSubgroup?: boolean;
    validRelativeTimeFrames: Array<number>;
    minTimeSliceSize?: number;
    maxSupportedTimeFrame?: number;
    resourceNames: DataSourceResources;
    filters?: {
        isActive?: boolean;
        ignoredStats?: boolean;
    };
}

type DataSourceDetailsDict = Dictionary<DataSourceDetails>;

let DATA_SOURCE_DETAILS = I.fromJS({
    radio: {
        name: 'Radio',
        fields: [
            'noise',
            'avg_rssi',
            'snr',
            'avg_rsrp',
            'avg_rsrq',
            'avg_sinr',
            'channel_utilisation',
            'avg_tx_bps',
            'avg_rx_bps',
            'cumulative_total_tx_bps',
            'cumulative_total_rx_bps',
            'avg_tx_rate',
            'avg_rx_rate',
            'tx_power',
            'channel',
        ],
        filterTypes: [
            FILTER_BY_RADIO_ID,
            FILTER_BY_RADIO_TECHNOLOGY_CATEGORY,
            FILTER_BY_RADIO_TECHNOLOGY_TYPE,
            FILTER_BY_IP_DEVICE_ID,
            FILTER_BY_IP_DEVICE_TYPE,
            FILTER_BY_IP_DEVICE_TAG_ID,
            FILTER_BY_ASSET_ID,
            FILTER_BY_ASSET_TYPE_ID,
            FILTER_BY_ASSET_TAG_ID,
            FILTER_BY_WIFI_CHANNEL,
        ],
        groups: [
            GROUP_BY_ALL,
            GROUP_BY_RADIO,
            GROUP_BY_RADIO_TECHNOLOGY_CATEGORY,
            GROUP_BY_RADIO_TECHNOLOGY_TYPE,
            GROUP_BY_IP_DEVICE,
            GROUP_BY_IP_DEVICE_TYPE,
            GROUP_BY_IP_DEVICE_TAG,
            GROUP_BY_ASSET,
            GROUP_BY_ASSET_TYPE,
            GROUP_BY_ASSET_TAG,
            GROUP_BY_WIFI_CHANNEL,
        ],
        defaultGroup: 'asset',
        validRelativeTimeFrames: [
            // Maybe this is okay now?
            // HACK: Removed 720 (12 hours) as it has unpredictable performance on some databases for radio stats
            1, 5, 15, 30, 60, 180, 360, 720, 1440, 10080, 40320, 120960, 262800,
        ].filter(filterAllowableIPDeviceStatValidRelativeTimeFrames),
        // tables
        resourceNames: createResourceDefinitionWithHourlyAnd5mRollup(
            'radioStats',
            ConfigService.ipDeviceStatDataFrequency,
            toSeconds(2, TimeUnit.DAYS),
            'radioStats5m',
            FIVE_MINUTE_SEPERATION,
            'radioStatsHourly',
            null,
        ),
        filters: {
            isActive: true,
            ignoredStats: true,
        },
        minTimeSliceSize: IP_DEVICE_STAT_MIN_TIME_SLICE_SIZE,
    },
    ip_device: {
        name: 'IP Device',
        fields: ['latency', 'connectivity'].concat(
            ConfigService.packetConfigs.map(({ logicalName }) => logicalName),
        ),
        filterTypes: [
            FILTER_BY_IP_DEVICE_ID,
            FILTER_BY_IP_DEVICE_TYPE,
            FILTER_BY_IP_DEVICE_TAG_ID,
            FILTER_BY_ASSET_ID,
            FILTER_BY_ASSET_TYPE_ID,
            FILTER_BY_ASSET_TAG_ID,
        ],
        groups: [
            GROUP_BY_ALL,
            GROUP_BY_IP_DEVICE,
            GROUP_BY_IP_DEVICE_TYPE,
            GROUP_BY_IP_DEVICE_TAG,
            GROUP_BY_ASSET,
            GROUP_BY_ASSET_TYPE,
            GROUP_BY_ASSET_TAG,
        ],
        defaultGroup: 'asset',
        validRelativeTimeFrames: [
            1, 5, 15, 30, 60, 180, 360, 720, 1440, 10080, 40320, 120960, 262800,
        ].filter(filterAllowableIPDeviceStatValidRelativeTimeFrames),
        resourceNames: createResourceDefinitionWithHourlyAnd5mRollup(
            'ipDeviceStats',
            ConfigService.ipDeviceStatDataFrequency,
            toSeconds(2, TimeUnit.DAYS),
            'ipDeviceStats5m',
            FIVE_MINUTE_SEPERATION,
            'ipDeviceStatsHourly',
            null,
        ),
        filters: {
            ignoredStats: true,
        },
        minTimeSliceSize: IP_DEVICE_STAT_MIN_TIME_SLICE_SIZE,
    },
    solar_controller_stat: {
        name: 'Solar Controller',
        fields: [
            'battery_temp',
            'output_power',
            'input_power',
            'panel_voltage',
            'battery_voltage',
            'panel_current',
            'battery_current',
        ],
        filterTypes: [
            FILTER_BY_IP_DEVICE_ID,
            FILTER_BY_IP_DEVICE_TYPE,
            FILTER_BY_IP_DEVICE_TAG_ID,
            FILTER_BY_ASSET_ID,
            FILTER_BY_ASSET_TYPE_ID,
            FILTER_BY_ASSET_TAG_ID,
        ],
        groups: [
            GROUP_BY_ALL,
            GROUP_BY_IP_DEVICE,
            GROUP_BY_IP_DEVICE_TYPE,
            GROUP_BY_IP_DEVICE_TAG,
            GROUP_BY_ASSET,
            GROUP_BY_ASSET_TYPE,
            GROUP_BY_ASSET_TAG,
        ],
        defaultGroup: 'asset',
        validRelativeTimeFrames: [
            1, 5, 15, 30, 60, 180, 360, 720, 1440, 10080, 40320, 120960, 262800,
        ],
        resourceNames: createResourceDefinitionWithHourlyRollup(
            'solarControllerStats',
            30,
            null,
            'solarControllerStatsHourly',
            null,
        ),
        minTimeSliceSize: 60,
    },
    hardware: {
        name: 'Hardware',
        fields: [
            'total_cpu_percentage',
            'total_ram_percentage',
            'total_storage_percentage',
            'load_1_min',
            'load_5_min',
            'load_15_min',
            'normalized_load_1_min',
            'normalized_load_5_min',
            'normalized_load_15_min',
        ],
        filterTypes: [
            FILTER_BY_IP_DEVICE_ID,
            FILTER_BY_IP_DEVICE_TYPE,
            FILTER_BY_IP_DEVICE_TAG_ID,
            FILTER_BY_ASSET_ID,
            FILTER_BY_ASSET_TYPE_ID,
            FILTER_BY_ASSET_TAG_ID,
        ],
        groups: [
            GROUP_BY_ALL,
            GROUP_BY_IP_DEVICE,
            GROUP_BY_IP_DEVICE_TYPE,
            GROUP_BY_IP_DEVICE_TAG,
            GROUP_BY_ASSET,
            GROUP_BY_ASSET_TYPE,
            GROUP_BY_ASSET_TAG,
        ],
        defaultGroup: 'asset',
        validRelativeTimeFrames: [
            1, 5, 15, 30, 60, 180, 360, 720, 1440, 10080, 40320, 120960, 262800,
        ],
        resourceNames: createResourceDefinitionWithHourlyRollup(
            'hardwareStats',
            8,
            null,
            'hardwareStatsHourly',
            null,
        ),
        minTimeSliceSize: 30,
    },
    process: {
        name: 'Process',
        fields: ['ram_used', 'cpu_percentage', 'ram_used_percentage'],
        filterTypes: [
            FILTER_BY_IP_DEVICE_ID,
            FILTER_BY_IP_DEVICE_TYPE,
            FILTER_BY_IP_DEVICE_TAG_ID,
            FILTER_BY_ASSET_ID,
            FILTER_BY_ASSET_TYPE_ID,
            FILTER_BY_ASSET_TAG_ID,
            'name',
        ],
        groups: [
            GROUP_BY_ALL,
            GROUP_BY_IP_DEVICE,
            GROUP_BY_IP_DEVICE_TYPE,
            GROUP_BY_IP_DEVICE_TAG,
            GROUP_BY_ASSET,
            GROUP_BY_ASSET_TYPE,
            GROUP_BY_ASSET_TAG,
        ],
        defaultGroup: 'process_name',
        validRelativeTimeFrames: [
            1, 5, 15, 30, 60, 180, 360, 720, 1440, 10080, 40320, 120960, 262800,
        ],
        resourceNames: createResourceDefinitionWithHourlyRollup(
            'processStats',
            8,
            null,
            'processStatsHourly',
            null,
        ),
        minTimeSliceSize: 30,
    },
    storage: {
        name: 'Storage',
        fields: ['storage_used_percentage', 'storage_total'],
        filterTypes: [
            FILTER_BY_IP_DEVICE_ID,
            FILTER_BY_IP_DEVICE_TYPE,
            FILTER_BY_IP_DEVICE_TAG_ID,
            FILTER_BY_ASSET_ID,
            FILTER_BY_ASSET_TYPE_ID,
            FILTER_BY_ASSET_TAG_ID,
            'name',
        ],
        groups: [
            GROUP_BY_ALL,
            GROUP_BY_IP_DEVICE,
            GROUP_BY_IP_DEVICE_TYPE,
            GROUP_BY_IP_DEVICE_TAG,
            GROUP_BY_ASSET,
            GROUP_BY_ASSET_TYPE,
            GROUP_BY_ASSET_TAG,
        ],
        defaultGroup: 'mount_point',
        validRelativeTimeFrames: [
            1, 5, 15, 30, 60, 180, 360, 720, 1440, 10080, 40320, 120960, 262800,
        ],
        resourceNames: createResourceDefinitionWithHourlyRollup(
            'storageStats',
            8,
            null,
            'storageStatsHourly',
            null,
        ),
        minTimeSliceSize: 30,
    },
    connections_as_client: {
        name: 'Connections as Client',
        fields: ['client_connection_time', 'unique_roams'],
        filterTypes: [
            FILTER_BY_IP_DEVICE_ID,
            FILTER_BY_IP_DEVICE_TYPE,
            FILTER_BY_IP_DEVICE_TAG_ID,
            FILTER_BY_ASSET_ID,
            FILTER_BY_ASSET_TYPE_ID,
            FILTER_BY_ASSET_TAG_ID,
        ],
        groups: [
            GROUP_BY_ALL,
            GROUP_BY_IP_DEVICE,
            GROUP_BY_IP_DEVICE_TYPE,
            GROUP_BY_IP_DEVICE_TAG,
            GROUP_BY_ASSET,
            GROUP_BY_ASSET_TYPE,
            GROUP_BY_ASSET_TAG,
        ],
        defaultGroup: 'asset',
        validRelativeTimeFrames: [
            5, 15, 30, 60, 180, 720, 1440, 10080, 40320, 120960, 262800,
        ].filter(filterAllowableIPDeviceStatValidRelativeTimeFrames),
        // maxNonHourlyTimeFrame: 360,
        resourceNames: createResourceDefinitionWithHourlyAnd5mRollup(
            'connectionsAsClient',
            ConfigService.ipDeviceStatDataFrequency,
            toSeconds(1, TimeUnit.DAYS),
            'connectionsAsClient5m',
            FIVE_MINUTE_SEPERATION,
            'connectionsAsClientHourly',
            null,
        ),
        filters: {
            ignoredStats: true,
        },
        minTimeSliceSize: IP_DEVICE_STAT_MIN_TIME_SLICE_SIZE,
    },
    connections_as_ap: {
        name: 'Connections as AP',
        fields: ['ap_connection_time', 'unique_clients'],
        filterTypes: [
            FILTER_BY_RADIO_ID,
            FILTER_BY_RADIO_TECHNOLOGY_TYPE,
            FILTER_BY_RADIO_TECHNOLOGY_CATEGORY,
            FILTER_BY_IP_DEVICE_ID,
            FILTER_BY_IP_DEVICE_TYPE,
            FILTER_BY_IP_DEVICE_TAG_ID,
            FILTER_BY_ASSET_ID,
            FILTER_BY_ASSET_TYPE_ID,
            FILTER_BY_ASSET_TAG_ID,
        ],
        groups: [
            GROUP_BY_ALL,
            GROUP_BY_RADIO,
            GROUP_BY_RADIO_TECHNOLOGY_CATEGORY,
            GROUP_BY_RADIO_TECHNOLOGY_TYPE,
            GROUP_BY_IP_DEVICE,
            GROUP_BY_IP_DEVICE_TYPE,
            GROUP_BY_IP_DEVICE_TAG,
            GROUP_BY_ASSET,
            GROUP_BY_ASSET_TYPE,
            GROUP_BY_ASSET_TAG,
        ],
        defaultGroup: 'asset',
        validRelativeTimeFrames: [
            5, 15, 30, 60, 180, 720, 1440, 10080, 40320, 120960, 262800,
        ].filter(filterAllowableIPDeviceStatValidRelativeTimeFrames),
        // maxNonHourlyTimeFrame: 360,
        resourceNames: createResourceDefinitionWithHourlyAnd5mRollup(
            'connectionsAsAP',
            ConfigService.ipDeviceStatDataFrequency,
            toSeconds(1, TimeUnit.DAYS),
            'connectionsAsAP5m',
            FIVE_MINUTE_SEPERATION,
            'connectionsAsAPHourly',
            null,
        ),
        filters: {
            ignoredStats: true,
        },
        minTimeSliceSize: IP_DEVICE_STAT_MIN_TIME_SLICE_SIZE,
    },
    connection_distance_as_client: {
        name: 'Connection Distance as Client',
        fields: ['distance_as_client'],
        filterTypes: [
            FILTER_BY_RADIO_ID,
            FILTER_BY_RADIO_TECHNOLOGY_CATEGORY,
            FILTER_BY_RADIO_TECHNOLOGY_TYPE,
            FILTER_BY_IP_DEVICE_ID,
            FILTER_BY_IP_DEVICE_TYPE,
            FILTER_BY_IP_DEVICE_TAG_ID,
            FILTER_BY_ASSET_ID,
            FILTER_BY_ASSET_TYPE_ID,
            FILTER_BY_ASSET_TAG_ID,
        ],
        groups: [
            GROUP_BY_ALL,
            GROUP_BY_RADIO,
            GROUP_BY_RADIO_TECHNOLOGY_CATEGORY,
            GROUP_BY_RADIO_TECHNOLOGY_TYPE,
            GROUP_BY_IP_DEVICE,
            GROUP_BY_IP_DEVICE_TYPE,
            GROUP_BY_IP_DEVICE_TAG,
            GROUP_BY_ASSET,
            GROUP_BY_ASSET_TYPE,
            GROUP_BY_ASSET_TAG,
        ],
        defaultGroup: 'asset',
        // more than 180 minutes will make the query
        // run too slow for any reasonably sized deployment.
        validRelativeTimeFrames: [5, 15, 30, 60, 180],
        // Comparator is >=. We want 180 minutes to use the high granularity
        // stats so the cutoff is 180 + 1
        // maxNonHourlyTimeFrame: 181,
        maxSupportedTimeFrame: 180,
        resourceNames: {
            default: 'connectionDistanceAsClient',
            preAggregated: [],
        },
        filters: {
            ignoredStats: true,
        },
    },
    connection_distance_as_ap: {
        name: 'Connection Distance as AP',
        fields: ['distance_as_ap'],
        filterTypes: [
            FILTER_BY_RADIO_ID,
            FILTER_BY_RADIO_TECHNOLOGY_CATEGORY,
            FILTER_BY_RADIO_TECHNOLOGY_TYPE,
            FILTER_BY_IP_DEVICE_ID,
            FILTER_BY_IP_DEVICE_TYPE,
            FILTER_BY_IP_DEVICE_TAG_ID,
            FILTER_BY_ASSET_ID,
            FILTER_BY_ASSET_TYPE_ID,
            FILTER_BY_ASSET_TAG_ID,
        ],
        groups: [
            GROUP_BY_ALL,
            GROUP_BY_RADIO,
            GROUP_BY_RADIO_TECHNOLOGY_CATEGORY,
            GROUP_BY_RADIO_TECHNOLOGY_TYPE,
            GROUP_BY_IP_DEVICE,
            GROUP_BY_IP_DEVICE_TYPE,
            GROUP_BY_IP_DEVICE_TAG,
            GROUP_BY_ASSET,
            GROUP_BY_ASSET_TYPE,
            GROUP_BY_ASSET_TAG,
        ],
        defaultGroup: 'asset',
        // more than 180 minutes will make the query
        // run too slow for any reasonably sized deployment.
        validRelativeTimeFrames: [5, 15, 30, 60, 180],
        // Comparator is >=. We want 180 minutes to use the high granularity
        // stats so the cut off is 180 + 1
        // maxNonHourlyTimeFrame: 181,
        maxSupportedTimeFrame: 180,
        resourceNames: {
            default: 'connectionDistanceAsAp',
            preAggregated: [],
        },
        filters: {
            ignoredStats: true,
        },
    },
    [DATA_SOURCE_TRIGGER]: {
        name: 'Trigger',
        fields: [FIELD_TRIGGER_TRIPPED_PERCENTAGE],
        filterTypes: [FILTER_BY_TRIGGER_ID],
        groups: [GROUP_BY_TRIGGER],
        defaultGroup: GROUP_BY_TRIGGER,
        validRelativeTimeFrames: [1, 5, 15, 30, 60, 180, 360, 1440],
        resourceNames: createResourceDefinitionWithHourlyRollup(
            'triggerStats',
            8, // 8 seconds per trigger stat, is a real bad estimate, it could be anything
            null,
            'triggerStatsHourly',
            null,
        ),
        filters: {},
    },
});

type ImmutableDataSourceDetailsDict = I.Map<string, I.Map<string, any>>;

// some object types are not yet part of dynamic stats to use as foreign keys - but we still want to know about them!
type NonDynamicStatForeignKeyFields = 'asset_type';

export interface GroupDetails {
    name: string;
    value: string;
    filterType: string;
    drillDownGroup: string;
    relationshipTable?: string;
    relationshipField?: string;
    targetAssetType?: string;
    // currently this is only used for looking up the color of the appropriate object
    //  because dynamic stats can "group by" foreign key fields meaning the grouping name
    //  doesn't explain what type of object that is
    // for example - you can group by "my_dyn_stat.my_special_fk_field" and this now has a grouping type of "asset"
    groupingObjectType: null | ForeignKeyFieldType | NonDynamicStatForeignKeyFields;
    allowSubgroup: boolean;
}

export interface FilterDetails {
    /**
     * The display name for this filter
     */
    name: string;

    /**
     * The logical name of this filter, send in the query params, stored in the JSON, etc.
     * NOTE: if the path property is also set, then value is stored in the dashboard item config
     *  but the path will be sent in the HTTP request
     */
    value: string;
    /**
     * If this filter is a raw text filter
     */
    text?: boolean;
    /**
     * The type of object this filter is for (if not a raw text filter)
     */
    imsObjectType?: string;

    /**
     * Used as part of a recursive relationship filter for relationship dynamic stats
     */
    relationshipTable?: string;
    /**
     * Used as part of a recursive relationship filter for relationship dynamic stats
     */
    relationshipField?: string;

    /**
     * Used as part of a recursive relationship filter for relationship dynamic stats
     */
    targetAssetType?: string;

    /**
     * How to prompt the user when selection an item of the given imsObjectType,
     * Will default to something like: "Select an Asset"
     */
    selectionPlaceholder?: string;

    /**
     * Only applies if the imsObjectType is "asset" (could be generalized later if required).
     * The available asset's to pick from will be filtered to only those whose type's name
     *  is one from the give case-sensistive, exact match, list.
     */

    filterByAssetTypeNames?: Array<string>;
}

type GroupDetailsMap = { [name: string]: GroupDetails };
export type FilterDetailsMap = { [name: string]: FilterDetails };

const groupDetails: GroupDetailsMap = {
    all: {
        name: 'All',
        value: 'all',
        filterType: 'all',
        drillDownGroup: 'asset',
        allowSubgroup: false,
        groupingObjectType: null,
    },
    radio: {
        name: 'Radio',
        value: 'radio',
        filterType: 'radio_id',
        drillDownGroup: 'radio',
        allowSubgroup: false,
        groupingObjectType: null, // technically probably 'radio' by we don't care about that for now
    },
    ip_device: {
        name: 'IP Device',
        value: 'ip_device',
        filterType: 'ip_device_id',
        drillDownGroup: 'ip_device',
        allowSubgroup: false,
        groupingObjectType: 'ip_device',
    },
    asset: {
        name: 'Asset',
        value: 'asset',
        filterType: 'asset_id',
        drillDownGroup: 'ip_device',
        allowSubgroup: false,
        groupingObjectType: 'asset',
    },
    asset_type: {
        name: 'Asset Type',
        value: 'asset_type',
        filterType: 'asset_type_id',
        drillDownGroup: 'asset',
        allowSubgroup: false,
        groupingObjectType: 'asset_type',
    },
    process_name: {
        name: 'Process',
        value: 'process_name',
        filterType: 'name',
        drillDownGroup: 'process_name',
        allowSubgroup: false,
        groupingObjectType: null,
    },
    mount_point: {
        name: 'Volume',
        value: 'mount_point',
        filterType: 'name',
        drillDownGroup: 'mount_point',
        allowSubgroup: false,
        groupingObjectType: null,
    },
    ip_device_log_ip_device_name: {
        name: 'IP Device',
        value: 'ip_device_log_ip_device_name',
        filterType: 'name',
        drillDownGroup: 'ip_device_log_ip_device_name',
        allowSubgroup: false,
        groupingObjectType: null,
    },
    [GROUP_BY_IP_DEVICE_TAG]: {
        name: 'IP Device Tag',
        value: GROUP_BY_IP_DEVICE_TAG,
        filterType: FILTER_BY_IP_DEVICE_TAG_ID,
        drillDownGroup: 'ip_device',
        allowSubgroup: false,
        groupingObjectType: null,
    },
    [GROUP_BY_ASSET_TAG]: {
        name: 'Asset Tag',
        value: GROUP_BY_ASSET_TAG,
        filterType: FILTER_BY_ASSET_TAG_ID,
        drillDownGroup: 'asset',
        allowSubgroup: false,
        groupingObjectType: null,
    },
    [GROUP_BY_IP_DEVICE_TYPE]: {
        name: 'IP Device Type',
        value: GROUP_BY_IP_DEVICE_TYPE,
        filterType: FILTER_BY_IP_DEVICE_TYPE,
        drillDownGroup: 'ip_device',
        allowSubgroup: false,
        groupingObjectType: null,
    },
    [GROUP_BY_TRIGGER]: {
        name: 'Trigger',
        value: GROUP_BY_TRIGGER,
        filterType: FILTER_BY_TRIGGER_ID,
        drillDownGroup: null,
        allowSubgroup: false,
        groupingObjectType: null,
    },
    [GROUP_BY_ZONE]: {
        name: 'Zone',
        value: GROUP_BY_ZONE,
        filterType: FILTER_BY_ZONE_ID,
        drillDownGroup: null,
        allowSubgroup: false,
        groupingObjectType: 'zone',
    },
    [GROUP_BY_ZONE_TYPE]: {
        name: 'Zone Type',
        value: GROUP_BY_ZONE_TYPE,
        filterType: FILTER_BY_ZONE_TYPE_ID,
        drillDownGroup: 'zone',
        allowSubgroup: false,
        groupingObjectType: null,
    },
    [GROUP_BY_RADIO_TECHNOLOGY_CATEGORY]: {
        name: 'Radio Technology Category',
        value: GROUP_BY_RADIO_TECHNOLOGY_CATEGORY,
        filterType: FILTER_BY_RADIO_TECHNOLOGY_CATEGORY,
        drillDownGroup: 'radio',
        allowSubgroup: false,
        groupingObjectType: null,
    },
    [GROUP_BY_RADIO_TECHNOLOGY_TYPE]: {
        name: 'Radio Technology Type',
        value: GROUP_BY_RADIO_TECHNOLOGY_TYPE,
        filterType: FILTER_BY_RADIO_TECHNOLOGY_TYPE,
        drillDownGroup: 'radio',
        allowSubgroup: false,
        groupingObjectType: null,
    },
    [GROUP_BY_WIFI_CHANNEL]: {
        name: 'Wifi Channel',
        value: GROUP_BY_WIFI_CHANNEL,
        filterType: FILTER_BY_WIFI_CHANNEL,
        drillDownGroup: 'radio',
        allowSubgroup: false,
        groupingObjectType: null,
    },
};

let GROUP_DETAILS = I.fromJS(groupDetails);

const filterDetails: FilterDetailsMap = {
    [FilterService.FILTER_BY_ASSET]: {
        name: 'Asset',
        value: FilterService.FILTER_BY_ASSET,
        imsObjectType: IMS_TYPE__ASSET,
    },
    [FilterService.FILTER_BY_ASSET_TYPE]: {
        name: 'Asset Type',
        value: FilterService.FILTER_BY_ASSET_TYPE,
        imsObjectType: IMS_TYPE__ASSET_TYPE,
    },
    [FilterService.FILTER_BY_ASSET_TAG]: {
        name: 'Asset Tag',
        value: FilterService.FILTER_BY_ASSET_TAG,
        imsObjectType: IMS_TYPE__ASSET_TAG,
    },
    [FilterService.FILTER_BY_IP_DEVICE]: {
        name: 'IP Device',
        value: FilterService.FILTER_BY_IP_DEVICE,
        imsObjectType: IMS_TYPE__IP_DEVICE,
    },
    [FilterService.FILTER_BY_IP_DEVICE_TYPE]: {
        name: 'IP Device Type',
        value: FilterService.FILTER_BY_IP_DEVICE_TYPE,
        imsObjectType: IMS_TYPE__IP_DEVICE_TYPE,
    },
    [FilterService.FILTER_BY_IP_DEVICE_TAG]: {
        name: 'IP Device Tag',
        value: FilterService.FILTER_BY_IP_DEVICE_TAG,
        imsObjectType: IMS_TYPE__IP_DEVICE_TAG,
    },
    [FilterService.FILTER_BY_RADIO]: {
        name: 'Radio',
        value: FilterService.FILTER_BY_RADIO,
        imsObjectType: IMS_TYPE__RADIO,
    },
    [FilterService.FILTER_BY_RADIO_TECHNOLOGY_CATEGORY]: {
        name: 'Radio Technology Category',
        value: FilterService.FILTER_BY_RADIO_TECHNOLOGY_CATEGORY,
        imsObjectType: IMS_TYPE__RADIO_TECHNOLOGY,
        selectionPlaceholder: 'Select a Radio Technology Category',
    },
    [FilterService.FILTER_BY_RADIO_TECHNOLOGY_TYPE]: {
        name: 'Radio Technology Type',
        value: FilterService.FILTER_BY_RADIO_TECHNOLOGY_TYPE,
        imsObjectType: IMS_TYPE__RADIO_TECHNOLOGY_TYPE,
    },
    [FilterService.FILTER_BY_ZONE]: {
        name: 'Zone',
        value: FilterService.FILTER_BY_ZONE,
        imsObjectType: IMS_TYPE__ZONE,
    },
    [FilterService.FILTER_BY_ZONE_TYPE]: {
        name: 'Zone Type',
        value: FilterService.FILTER_BY_ZONE_TYPE,
        imsObjectType: IMS_TYPE__ZONE_TYPE,
    },
    [FilterService.FILTER_BY_TRIGGER]: {
        name: 'Trigger',
        value: FilterService.FILTER_BY_TRIGGER,
        imsObjectType: IMS_TYPE__TRIGGER,
    },
    // currently only used for process name
    [FilterService.FILTER_BY_NAME]: {
        name: 'Name',
        value: FilterService.FILTER_BY_NAME,
        text: true,
    },
    // ahh, not really used as a real filter, only when you "select" a connection from the map directly
    [FilterService.FILTER_BY_CONNECTION]: {
        name: 'Connection',
        value: FilterService.FILTER_BY_CONNECTION,
    },
    // not a selectable filter in the settings panel. mainly for filtering by clicking the chart/status directly
    [FilterService.FILTER_BY_SUBGROUP_NAME]: {
        name: 'Subgroup',
        value: FilterService.FILTER_BY_SUBGROUP_NAME,
    },
    [FilterService.FILTER_BY_WIFI_CHANNEL]: {
        name: 'Wifi Channel',
        value: FilterService.FILTER_BY_WIFI_CHANNEL,
        text: true,
    },
};
let FILTER_DETAILS = I.fromJS(filterDetails);

const CHART_DISPLAY_TYPE_NAMES = I.Map({
    line: 'Trend Line',
    bar: 'Trend Bar',
    accumulated_bar: 'Heaped Bar',
});

const STATUS_ITEM_SIZES = I.List.of(1, 2, 3, 4, 6);

const STATUS_ITEM_SIZE_NAMES = I.Map([
    [1, 'Extra Small'],
    [2, 'Small'],
    [3, 'Medium'],
    [4, 'Large'],
    [6, 'Extra Large'],
]);

const RELATIVE_START_TIME_NAMES = I.Map([
    [1, '1 Minute before end time'],
    [5, '5 Minutes before end time'],
    [15, '15 Minutes before end time'],
    [30, '30 Minutes before end time'],
    [60, '1 Hour before end time'],
    [180, '3 Hours before end time'],
    [360, '6 Hours before end time'],
    [720, '12 Hours before end time'],
    [1440, '1 Day before end time'],
    [10080, '1 Week before end time'],
    [40320, '1 Month before end time'],
    [120960, '3 Months before end time'],
    [262800, '6 Months before end time'],
]);

const RELATIVE_END_TIME_NAMES = I.Map([
    [1, '1 Minute before timeline end'],
    [5, '5 Minutes before timeline end'],
    [15, '15 Minutes before timeline end'],
    [30, '30 Minutes before timeline end'],
    [60, '1 Hour before timeline end'],
    [180, '3 Hours before timeline end'],
    [360, '6 Hours before timeline end'],
    [720, '12 Hours before timeline end'],
    [1440, '1 Day before timeline end'],
    [10080, '1 Week before timeline end'],
    [40320, '1 Month before timeline end'],
    [120960, '3 Months before timeline end'],
    [262800, '6 Months before timeline end'],
]);

// Used for string formatting the contents of the select dropdown for
// start and end times for the dashboard item
const NON_RELATIVE_PRESET = 'non_relative';
const RELATIVE_PRESET = 'relative';

export enum StartTimesSetting {
    // A custom absolute time
    CUSTOM = 'custom',
    // sync with timeline start
    SYNC = 'sync',
    // relative before end with a custom time duration
    RELATIVE_CUSTOM = 'relative_custom',
    // relative with a preset
    RELATIVE = 'relative',
}

const NON_RELATIVE_PRESET_START_TIMES = I.List.of(
    StartTimesSetting.CUSTOM,
    StartTimesSetting.SYNC,
    StartTimesSetting.RELATIVE_CUSTOM,
);

const NON_RELATIVE_PRESET_START_TIME_NAMES = I.Map({
    [StartTimesSetting.CUSTOM]: 'Custom',
    [StartTimesSetting.SYNC]: 'Sync with timeline start',
    [StartTimesSetting.RELATIVE_CUSTOM]: 'Custom before end time',
});

export enum EndTimesSetting {
    // A custom absolute time
    CUSTOM = 'custom',
    // sync with timeline end
    SYNC = 'sync',
    // before timeline end with a custom time duration
    RELATIVE_CUSTOM = 'relative_custom',
    // before cursor with a preset time duration
    RELATIVE = 'relative',
}

const NON_RELATIVE_PRESET_END_TIMES = I.List.of(
    EndTimesSetting.CUSTOM,
    EndTimesSetting.SYNC,
    EndTimesSetting.RELATIVE_CUSTOM,
);

const NON_RELATIVE_PRESET_END_TIME_NAMES = I.Map({
    [EndTimesSetting.CUSTOM]: 'Custom',
    [EndTimesSetting.SYNC]: 'Sync with timeline end',
    [EndTimesSetting.RELATIVE_CUSTOM]: 'Custom before timeline end',
});

let DEFAULT_CONFIG = I.fromJS({
    aggregationType: 'avg',
    objType: 'dashboard_item_config',
    objVersion: 3,
    itemType: ITEM_TYPE_CHART,
    useIMSQL: false,
    imsql: null,
    autoRefreshRateSeconds: null,
    useAutoTimeSliceSize: true,
    useGeneratedHeader: true,
    userHeader: 'Untitled Dashboard Panel',
    filterUsingQuicklist: false,
    includeNullSeries: false,
    filterSet: {},
    group: null,
    useSubgroup: true,
    includePartialTimeSlice: false,
    dataField: 'connectivity',
    dataSource: 'ip_device',
    startTime: StartTimesSetting.SYNC,
    startTimeRelativeAmount: 15,
    startTimestamp: null,
    endTime: EndTimesSetting.SYNC,
    endTimestamp: null,
    ignoredStats: 'hide',
    labelDirections: 'horizontal',
    showAllLabels: false,
    isActive: 'active',
    statusItemSize: 3,
    chartDisplayType: 'line',
    isStacked: false,
    postAggregationFilters: { comparisonOperator: null, filterValue: null, filterKey: null },
    yAxisValues: {
        domainValues: [undefined, undefined],
        domainUnit: '',
        minAuto: true,
        maxAuto: true,
    },
    hiddenColumns: [],
} as VisConfig);

export type ImmutableFilterSet = I.Map<string, I.List<number | string>>;
export type MutableFilterSet = { [x: string]: Array<number | string> };

interface YAxisValues {
    domainValues: number[];
    domainUnit: string;
    minAuto: boolean;
    minValue?: string | number | Date;
    minUnit?: string;
    maxAuto: boolean;
    maxValue?: string | number | Date;
    maxUnit?: string;
}

export interface VisConfig {
    aggregationType?: string;
    autoRefreshRateSeconds: number;
    chartDisplayType: string;
    isStacked: boolean;
    customTimeSliceSize?: number;
    dataField: string;
    dataSource: string;
    endTime: EndTimesSetting | string;
    // time duration in minutes - used for the presets
    endTimeRelativeAmount?: number | string;
    // time duration in seconds
    endTimeRelativeCustomAmount?: number;
    endTimestamp: string;
    filterSet: MutableFilterSet;
    filterUsingQuicklist: boolean;
    group: string;
    ignoredStats: 'hide' | 'show' | 'all';
    labelDirections?: 'horizontal' | 'vertical';
    showAllLabels?: boolean;
    imsql: string;
    includeNullSeries: boolean;
    isActive: 'active' | 'inactive' | 'all';
    itemType: string;
    objType: string;
    objVersion: number;
    postAggregationFilters?: {
        comparisonOperator: keyof Operations;
        filterValue: string | number | Date;
        filterKey: string;
        filterDisplayUnit: string;
    };
    startTime: StartTimesSetting | string;
    // time duration in minutes - used for the presets
    startTimeRelativeAmount?: number | string;
    // time duration in seconds
    startTimeRelativeCustomAmount?: number;
    startTimestamp: string;
    statusItemSize: number;
    useAutoTimeSliceSize: boolean;
    useGeneratedHeader: boolean;
    useIMSQL: boolean;
    userHeader: string;
    useSubgroup: boolean;
    yAxisValues?: YAxisValues;
    isRateMode?: boolean;
    hiddenColumns?: Array<string>;
    includePartialTimeSlice: boolean;
}

export type ImmutableVisConfig = I.Map<string, any>;

/**
 * A normalized config is a layer below the VisConfig which doesn't have a concept of other IMS services.
 * This interface usually acts as an intermediate data structure between the vis config and the data request
 * from the API.
 *
 * This normalization means these objects are short lived (don't persist them anywhere) because they are only 'valid'
 *  at the moment they are created (normalized)
 */
export interface NormalizedVisConfig {
    startMoment: Moment;
    endMoment: Moment;
    filterSet: MutableFilterSet;
    dataSource: string;
    dataSourceResource: string;
    dataField: string;
    group: string;
    useSubgroup: boolean;
    aggregationType: string;
    ignoredStats: 'hide' | 'show' | 'all';
    labelDirections?: 'horizontal' | 'vertical';
    showAllLabels?: boolean;
    isActive: 'active' | 'inactive' | 'all';
    timeSliceSize: number;
    itemType: string;
    statusItemSize: number;
    chartDisplayType: string;
    isStacked: boolean;
    autoRefreshRateSeconds: number;
    useIMSQL: boolean;
    imsql: string;
    filterUsingQuicklist: boolean;
    includeNullSeries: boolean;
    yAxisValues?: YAxisValues;
    isRateMode?: boolean;
    hiddenColumns?: Array<string>;
    includePartialTimeSlice: boolean;
}

// Private static variable
// Used by the report.html to always disable pagination.
// Performance will drop but it means we don't need to worry about showing legends or
// showing a partial chart.
let forceDisablePagination = false;

// This service:
//  * Generates dashboard item header strings
//  * Stores dashboard item settings options
export class DashboardItemService {
    histories;
    private _historyUpdatedEvent: EventRegistry;
    private _imsItemTypeRegistry: ImsItemTypeRegistry;
    private _dynamicStatSchemaCollection: DynamicStatSchemaCollection;
    private _resource; // Angular resource factory
    private _dashboardItemServiceChangeEvent: CachedEventRegistry;
    private _colorRendererService: ColorRendererService;
    private _apiUrlRootDashboard: string;
    private _userGeneratedConfigService: UserGeneratedConfigService;
    private _configService: ConfigService;

    constructor(
        dynamicStatSchemaCollection: DynamicStatSchemaCollection,
        imsItemTypeRegistry: ImsItemTypeRegistry,
        $resource,
        colorRendererService: ColorRendererService,
        apiUrlRootDashboard: string,
        userGeneratedConfigService: UserGeneratedConfigService,
        restrictionsService: AbstractRestrictionsService,
        configService: ConfigService,
    ) {
        this._historyUpdatedEvent = new EventRegistry();
        this._dashboardItemServiceChangeEvent = new CachedEventRegistry();
        this._imsItemTypeRegistry = imsItemTypeRegistry;
        this._dynamicStatSchemaCollection = dynamicStatSchemaCollection;
        this._resource = $resource;
        this._colorRendererService = colorRendererService;
        this.histories = {};
        this._apiUrlRootDashboard = apiUrlRootDashboard;
        this._userGeneratedConfigService = userGeneratedConfigService;
        this._configService = configService;

        const dashboardRelationshipOptions = ConfigService.dashboardRelationshipOptions || [];

        imsItemTypeRegistry.addOnImsItemTypeRegistryReady(() => {
            dynamicStatSchemaCollection.getAllAsArrayAsync().then(async () => {
                this.loadDynamicStatTableDataSets(dashboardRelationshipOptions);
                await this._dashboardItemServiceChangeEvent.notifyListeners();

                // once the dynamic stat schemas have been mixed in, we can start listening to changes in
                //  app restrictions, and apply those as necessary...
                // TODO there's a bug within, where restrictions can only get MORE restrictive between reloads
                //  and no less restrictive because we keep stripping stuff from the global objects, but not adding them back
                //  as restrictions changes
                restrictionsService.subscribeToChanges((restrictions) => {
                    const isIPDeviceEnabled = isIPDevicesEnabled(restrictions);
                    const isTriggerEnabled = isTriggersEnabled(restrictions);
                    const isZoneEnabled = isZonesEnabled(restrictions);
                    this.applyRestrictionsToDashboardDataSourceGlobals(
                        isIPDeviceEnabled,
                        isTriggerEnabled,
                        isZoneEnabled,
                    );
                    this._dashboardItemServiceChangeEvent.notifyListeners();
                });
            });
        });

        this._userGeneratedConfigService.onConfigLoad(this.mutateFieldDetailsDict);
        this._userGeneratedConfigService.onConfigChange(this.mutateFieldDetailsDict);
    }

    private applyRestrictionsToDashboardDataSourceGlobals(
        isIPDeviceEnabled: boolean,
        isTriggersEnabled: boolean,
        isZonesEnables: boolean,
    ) {
        const dataSourceNamesToRemove = [];

        let dataSourceDetails: DataSourceDetailsDict = DATA_SOURCE_DETAILS.toJS();

        if (!isIPDeviceEnabled) {
            // remove this set of data sources if IP Device is disabled
            dataSourceNamesToRemove.push(
                'radio',
                'ip_device',
                'hardware',
                'process',
                'storage',
                'connections_as_client',
                'connections_as_ap',
                'connection_distance_as_client',
                'connection_distance_as_ap',
                'solar_controller_stat',
            );

            const ipDeviceTypeDataSourceNames = Object.entries(dataSourceDetails)
                .filter(([_, value]) =>
                    [FIELD_SCHEMAS__TYPE_IP_DEVICE, FIELD_SCHEMAS__TYPE_RADIO].includes(
                        value.parentType,
                    ),
                )
                .map(([key, _]) => key);
            dataSourceNamesToRemove.push(...ipDeviceTypeDataSourceNames);
        }

        if (!isTriggersEnabled) {
            dataSourceNamesToRemove.push(DATA_SOURCE_TRIGGER);
        }

        if (!isZonesEnables) {
            const zoneTypeDataSourceNames = Object.entries(dataSourceDetails)
                .filter(([_, value]) => value.parentType === FIELD_SCHEMAS__TYPE_ZONE)
                .map(([key, _]) => key);
            dataSourceNamesToRemove.push(...zoneTypeDataSourceNames);
        }

        if (!dataSourceNamesToRemove.length) {
            // if we've got nothing to remove, just don't touch anything
            //  so the default config remains exactly the same (connectivity as default)
            return;
        }

        // remove entire data sources we don't want
        dataSourceDetails = _.omitBy(dataSourceDetails, (value, key) =>
            dataSourceNamesToRemove.includes(key),
        );
        DATA_SOURCE_DETAILS = I.fromJS(dataSourceDetails);

        DATA_SOURCES = I.fromJS(
            // no idea why omit is turning our array into an object, thanks for nothing!
            Object.values(
                _.omitBy(DATA_SOURCES.toJS(), (value) => dataSourceNamesToRemove.includes(value)),
            ),
        );

        // update our default config to make sure it's a valid choice still
        const [firstDataSource, firstDataSourceDetails] = Object.entries<DataSourceDetails>(
            DATA_SOURCE_DETAILS.toJS(),
        )[0];
        DEFAULT_CONFIG = DEFAULT_CONFIG.set('dataField', firstDataSourceDetails.fields[0]).set(
            'dataSource',
            firstDataSource,
        );
    }

    loadDynamicStatTableDataSets(
        dashboardRelationshipOptions: Array<DashboardRelationshipOption> = [],
    ): DynamicStatTableDataSets {
        // Dynamic field details merging in with standard field details
        const dynamicFieldDetails: FieldDetailsDict = {};
        // Dynamic field type details merging in with standard field type details
        const dynamicFieldTypeDetails: FieldTypeDetailsDict = {};
        // Dynamic dataSource details list merging in with standard dataSource details
        const dynamicDataSourceDetails: DataSourceDetailsDict = {};
        // Dynamic dataSources list to merge with standard dataSources list
        const dynamicDataSources = [];
        // Dynamic group details list merging in with standard group details
        const dynamicGroupDetails: GroupDetailsMap = {};
        const dynamicFilterDetails: FilterDetailsMap = {};

        const isUnChartableDataType = (type) =>
            [
                FIELD_SCHEMAS__TYPE_IMAGE,
                FIELD_SCHEMAS__TYPE_FILE,
                FIELD_SCHEMAS__TYPE_LON_LAT,
                FIELD_SCHEMAS__TYPE_POINT,
                FIELD_SCHEMAS__TYPE_POLYGON,
                FIELD_SCHEMAS__TYPE_BOOLEAN,
            ].includes(type);

        const dynamicStatSchemas = this._imsItemTypeRegistry.getRegisteredDynamicStatSchemas();
        // Adds the aggregate endpoints for dynamic stats to be used by the dashboard
        createDynamicStatAggResources(
            this._resource,
            dynamicStatSchemas,
            this._apiUrlRootDashboard,
        );

        for (const option of dashboardRelationshipOptions) {
            dynamicFilterDetails[option.name] = {
                name: option.display_name,
                value: option.name,
                relationshipTable: option.relationship_table,
                relationshipField: option.relationship_field,
                targetAssetType: option.target_asset_type_name,
                imsObjectType: IMS_TYPE__ASSET,
                filterByAssetTypeNames:
                    option.filter_by_asset_type_names && option.filter_by_asset_type_names.length
                        ? option.filter_by_asset_type_names
                        : null,
                selectionPlaceholder: option.selection_placeholder,
            } as FilterDetails;
            dynamicGroupDetails[option.name] = {
                name: option.display_name,
                value: option.name,
                relationshipTable: option.relationship_table,
                relationshipField: option.relationship_field,
                targetAssetType: option.target_asset_type_name,
                drillDownGroup: null,
                filterType: null,
                groupingObjectType: IMS_TYPE__ASSET,
                allowSubgroup: true,
            };
        }

        dynamicStatSchemas.forEach((dynamicStatSchema) => {
            const registeredImsType: ImsItemTypeDetails = this._imsItemTypeRegistry.getTypeDetails(
                dynamicStatSchema.name,
            );
            const statDefinition: StatDefinition = registeredImsType.statDefinition;
            const field_schemas = statDefinition.field_schemas;
            const fieldNames = [];

            const extraGroupNames: Set<string> = new Set();
            let hasSubgroup = false;

            for (const name of Object.keys(field_schemas)) {
                const fieldSchema: FieldSchema = field_schemas[name];

                // Use the schema name as a namespace to stop conflicts where two dynamic stats
                // tables have the same name
                const fieldName = `${dynamicStatSchema.name}.${name}`;

                if (isUnChartableDataType(fieldSchema.type)) {
                    continue;
                }

                // Handle string data types
                if (!name || fieldSchema.type === FIELD_SCHEMAS__TYPE_STRING) {
                    // Strings can be subgroups. Add as a subgroup if is_subgroup is set to true
                    if (fieldSchema.is_subgroup) {
                        dynamicGroupDetails[fieldName] = {
                            name: fieldSchema.display_name ? fieldSchema.display_name : name,
                            value: fieldName,
                            filterType: name,
                            drillDownGroup: statDefinition.parent_type,
                            groupingObjectType: null,
                            allowSubgroup: true,
                        };
                        extraGroupNames.add(fieldName);
                        hasSubgroup = true;
                    }

                    // Continue, as there is nothing else to do for strings (yet)
                    continue;
                }

                // If it is a foreign key field, add it as an available grouping option
                if (isForeignKeyField(fieldSchema.type)) {
                    // Add group
                    dynamicGroupDetails[fieldName] = {
                        name: fieldSchema.display_name ? fieldSchema.display_name : name,
                        value: fieldName,
                        filterType: name,
                        drillDownGroup: statDefinition.parent_type,
                        // the value of the grouping will be the field itself
                        //  while this grouping object type will be the type of object the field is tracking
                        //  eg, asset, zone, etc.
                        groupingObjectType: fieldSchema.type,
                        allowSubgroup: !!fieldSchema.is_subgroup,
                    };

                    extraGroupNames.add(fieldName);
                } else {
                    // Otherwise, add it as a selectable field
                    fieldNames.push(fieldName);

                    function getAggTypesForSchema(fieldSchema: FieldSchema): AggregationType[] {
                        const stdAggregationTypes: AggregationType[] = [
                            'avg',
                            'sum',
                            'count',
                            'max',
                            'min',
                            'cumulative_sum',
                        ];

                        if (
                            fieldSchema.type == 'number' &&
                            fieldSchema.number_display_format == 'symbol'
                        ) {
                            return ['max', 'min'];
                        } else if (fieldSchema.type === FIELD_SCHEMAS__TYPE_TENLOGTEN) {
                            return [...stdAggregationTypes, 'log_sum', 'log_avg'];
                        } else {
                            return stdAggregationTypes;
                        }
                    }

                    const aggregationTypes = getAggTypesForSchema(fieldSchema);

                    // Add the suffix to aggregation types
                    const suffix: string = UnitService.getBaseSuffix(fieldSchema.units);
                    const aggTypeSuffixLookup: { [key in AggregationType]: string } = {
                        avg: suffix,
                        sum: suffix,
                        count: '',
                        max: suffix,
                        min: suffix,
                        cumulative_sum: suffix,
                        percentage: suffix,
                        log_sum: '',
                        log_avg: '',
                    } as const;
                    const aggregationTypesSuffix: string[] = aggregationTypes.map(
                        (aggType) => aggTypeSuffixLookup[aggType],
                    );

                    // Add field
                    dynamicFieldDetails[fieldName] = {
                        name: fieldSchema.display_name ? fieldSchema.display_name : name,
                        value: name,
                        type: fieldName,
                        unit: fieldSchema.units || { suffix: '' },
                        aggregationTypes,
                        aggregationTypesSuffix,
                    };

                    // Add fields details
                    dynamicFieldTypeDetails[fieldName] = {
                        name: fieldSchema.display_name ? fieldSchema.display_name : name,
                        chartYMax: 0,
                        chartYMin: 0,
                    };
                }

                this._colorRendererService.setColorRenderer(fieldName, fieldSchema.color_config);
            }

            // Calculate filter types
            let filterTypes = [];

            if (statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_RADIO) {
                filterTypes = [
                    FILTER_BY_RADIO_ID,
                    FILTER_BY_RADIO_TECHNOLOGY_CATEGORY,
                    FILTER_BY_RADIO_TECHNOLOGY_TYPE,
                    FILTER_BY_IP_DEVICE_ID,
                    FILTER_BY_IP_DEVICE_TYPE,
                    FILTER_BY_IP_DEVICE_TAG_ID,
                    FILTER_BY_ASSET_ID,
                    FILTER_BY_ASSET_TYPE_ID,
                    FILTER_BY_ASSET_TAG_ID,
                    FILTER_BY_WIFI_CHANNEL,
                ];
            } else if (statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_IP_DEVICE) {
                filterTypes = [
                    FILTER_BY_IP_DEVICE_ID,
                    FILTER_BY_IP_DEVICE_TYPE,
                    FILTER_BY_IP_DEVICE_TAG_ID,
                    FILTER_BY_ASSET_ID,
                    FILTER_BY_ASSET_TYPE_ID,
                    FILTER_BY_ASSET_TAG_ID,
                ];
            } else if (statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ASSET) {
                filterTypes = [FILTER_BY_ASSET_ID, FILTER_BY_ASSET_TYPE_ID, FILTER_BY_ASSET_TAG_ID];

                if (hasSubgroup) {
                    filterTypes.push(FILTER_BY_SUBGROUP_NAME);
                }

                for (const option of dashboardRelationshipOptions) {
                    if (option.available_schemas.includes(dynamicStatSchema.name)) {
                        filterTypes.push(option.name);
                    }
                }
            } else if (statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ZONE) {
                filterTypes = [FILTER_BY_ZONE_ID, FILTER_BY_ZONE_TYPE_ID];
            }

            // Calculate groups
            let groups = [];
            if (statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_RADIO) {
                groups = [
                    GROUP_BY_ALL,
                    GROUP_BY_RADIO,
                    GROUP_BY_RADIO_TECHNOLOGY_CATEGORY,
                    GROUP_BY_RADIO_TECHNOLOGY_TYPE,
                    GROUP_BY_IP_DEVICE,
                    GROUP_BY_IP_DEVICE_TYPE,
                    GROUP_BY_IP_DEVICE_TAG,
                    GROUP_BY_ASSET,
                    GROUP_BY_ASSET_TYPE,
                    GROUP_BY_ASSET_TAG,
                    GROUP_BY_WIFI_CHANNEL,
                ];
            } else if (statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_IP_DEVICE) {
                groups = [
                    GROUP_BY_ALL,
                    GROUP_BY_IP_DEVICE,
                    GROUP_BY_IP_DEVICE_TYPE,
                    GROUP_BY_IP_DEVICE_TAG,
                    GROUP_BY_ASSET,
                    GROUP_BY_ASSET_TYPE,
                    GROUP_BY_ASSET_TAG,
                ];
            } else if (statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ASSET) {
                groups = [GROUP_BY_ALL, GROUP_BY_ASSET, GROUP_BY_ASSET_TYPE, GROUP_BY_ASSET_TAG];

                for (const option of dashboardRelationshipOptions) {
                    if (option.available_schemas.includes(dynamicStatSchema.name)) {
                        groups.push(option.name);
                    }
                }
            } else if (statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ZONE) {
                groups = [GROUP_BY_ALL, GROUP_BY_ZONE, GROUP_BY_ZONE_TYPE];
            }

            groups.push(...extraGroupNames);

            // Add data source
            dynamicDataSources.push(dynamicStatSchema.name);

            const rollups = dynamicStatSchema.rollups;
            const timeSliceMinsArray = rollups.map((rollup) => rollup.time_slice_mins);

            const preAggregated: Array<DataSourceResource> = timeSliceMinsArray.map(
                (aggMinutes) => {
                    return {
                        name: `${dynamicStatSchema.name}_${aggMinutes}m`,
                        recommendedMaxTimeFrameSeconds: calculateMaxRecommendedTimeFrameInSeconds(
                            aggMinutes * 60,
                            MAX_RECOMMENDED_STATS_PER_GRAPH,
                        ),
                        aggSeconds: aggMinutes * 60,
                    };
                },
            );

            // if it's 0 or null, assume 8!
            const freq = dynamicStatSchema.data_frequency || 8;
            const defaultRecommendedMaxTimeFrameSeconds = calculateMaxRecommendedTimeFrameInSeconds(
                freq,
                MAX_RECOMMENDED_STATS_PER_GRAPH,
            );

            // Add data source details
            dynamicDataSourceDetails[dynamicStatSchema.name] = {
                name: statDefinition.display_name,
                fields: fieldNames,
                filterTypes: filterTypes,
                groups: groups,
                defaultGroup: 'asset',
                parentType: statDefinition.parent_type,
                hasSubgroup: hasSubgroup,
                validRelativeTimeFrames: [
                    1, 5, 15, 30, 60, 180, 360, 720, 1440, 10080, 40320, 120960, 262800,
                ].filter((x) => {
                    // only include time frames that should show at least two data points
                    return x >= (statDefinition.data_frequency / 60) * 2;
                }),
                resourceNames: {
                    default: dynamicStatSchema.name,
                    preAggregated: preAggregated,
                    defaultRecommendedMaxTimeFrameSeconds,
                },
                filters: {},
                minTimeSliceSize: dynamicStatSchema.data_frequency,
            };
        });

        FIELD_DETAILS = I.fromJS(Object.assign(fieldDetails, dynamicFieldDetails));
        FIELD_TYPE_DETAILS = I.fromJS(Object.assign(fieldTypeDetails, dynamicFieldTypeDetails));
        DATA_SOURCE_DETAILS = I.fromJS(
            Object.assign(DATA_SOURCE_DETAILS.toJS(), dynamicDataSourceDetails),
        );
        DATA_SOURCES = I.fromJS(DATA_SOURCES.toJS().concat(dynamicDataSources));
        GROUP_DETAILS = I.fromJS(Object.assign(groupDetails, dynamicGroupDetails));
        FILTER_DETAILS = I.fromJS(Object.assign(filterDetails, dynamicFilterDetails));

        return {
            fieldDetails: dynamicFieldDetails,
            fieldTypeDetails: dynamicFieldTypeDetails,
            dataSourceDetails: dynamicDataSourceDetails,
            dataSources: dynamicDataSources,
        };
    }

    mutateFieldDetailsDict(imsConfig: ImsUserConfig) {
        updateFieldDetailsWithUtilisation(imsConfig.throughputAsBits);
    }

    static set forceDisablePagination(value: boolean) {
        forceDisablePagination = value;
    }

    static get maxTrendDatasetSize() {
        return MAX_TREND_DATASET_SIZE;
    }

    static get defaultConfig() {
        return DEFAULT_CONFIG;
    }

    static get itemTypes() {
        return ITEM_TYPES;
    }

    static get itemTypeDetails() {
        return ITEM_TYPE_DETAILS;
    }

    static get dataSources() {
        return DATA_SOURCES;
    }

    static get dataSourceDetails(): ImmutableDataSourceDetailsDict {
        return DATA_SOURCE_DETAILS;
    }

    static get fieldDetails() {
        return FIELD_DETAILS;
    }

    static get fieldTypeDetails() {
        return FIELD_TYPE_DETAILS;
    }

    static get groupDetails() {
        return GROUP_DETAILS;
    }

    static get filterDetails() {
        return FILTER_DETAILS;
    }

    static get aggregationTypeOptions() {
        return AGGREGATION_TYPE_OPTIONS;
    }

    static get chartDisplayTypes() {
        return CHART_DISPLAY_TYPES;
    }

    static get chartDisplayTypeNames() {
        return CHART_DISPLAY_TYPE_NAMES;
    }

    static get autoRefreshRateSecondsOptions() {
        return AUTO_REFRESH_RATE_SECONDS_OPTIONS;
    }

    static get autoRefreshRateSecondsOptionsNames() {
        return AUTO_REFRESH_RATE_SECONDS_NAME_OPTIONS;
    }

    static get statusItemSizes() {
        return STATUS_ITEM_SIZES;
    }

    static get statusItemSizeNames() {
        return STATUS_ITEM_SIZE_NAMES;
    }

    static get ignoredStatsOptions() {
        return IGNORED_STAT_OPTIONS;
    }

    static get ignoredStatsOptionNames() {
        return IGNORED_STAT_NAME_OPTIONS;
    }

    static get labelDirectionsOptions() {
        return LABEL_DIRECTION_OPTIONS;
    }

    static get labelDirectionsOptionNames() {
        return LABEL_DIRECTION_NAME_OPTIONS;
    }

    static get isActiveOptions() {
        return IS_ACTIVE_OPTIONS;
    }

    static get isActiveOptionsNames() {
        return IS_ACTIVE_NAME_OPTIONS;
    }

    static getFieldsByFieldType(fieldType: string) {
        return DashboardItemService.fieldDetails
            .filter((details, field) => details.get('type') === fieldType)
            .keySeq();
    }

    static getFilterDetailsAvailableForDataSource(
        dataSource: string,
        timeSliceSize?: number,
        dashboardChartType?: string,
    ): FilterDetailsMap {
        const filterTypes = DashboardItemService.dataSourceDetails.getIn([
            dataSource,
            'filterTypes',
        ]);

        const excludeFilterTypes = [] as string[];
        if (!ConfigService.enableChartFilterByWifiOptions) {
            excludeFilterTypes.push(FILTER_BY_RADIO_TECHNOLOGY_CATEGORY);
            excludeFilterTypes.push(FILTER_BY_RADIO_TECHNOLOGY_TYPE);
            excludeFilterTypes.push(FILTER_BY_WIFI_CHANNEL);
        } else if (
            (timeSliceSize && timeSliceSize >= WIFI_CHANNEL_TIMESLICE_THRESHOLD) ||
            ![CHART_DISPLAY_TYPE_LINE, CHART_DISPLAY_TYPE_BAR].includes(dashboardChartType ?? '')
        ) {
            //Filter by wifi channel only available for trend line/bar dashboard charts where timeslice < threshold
            excludeFilterTypes.push(FILTER_BY_WIFI_CHANNEL);
        }

        const availableFilterDetails = DashboardItemService.filterDetails.filter(
            (value: any, key: any) =>
                filterTypes && filterTypes.includes(key) && !excludeFilterTypes.includes(key),
        );
        return availableFilterDetails.toJS();
    }

    static filterCollectionByPagination(
        collection: Array<any>,
        groupIdListToHide: Array<any>,
        currentPage: number,
        pageSize: number | string,
    ): Array<any> {
        if (!collection || !collection.length) {
            return [];
        }
        // make them just a list of strings
        const idsToHide = (groupIdListToHide || []).map((id) => id.group_id.toString());
        // Only store the data items that are selected for display here
        const partiallyFilteredCollection = collection.filter(
            (dataItem) => !idsToHide.includes(dataItem.group_id.toString()),
        );
        // handle pagination
        const size = pageSize == PAGE_SIZE_ALL ? null : (pageSize as number);
        return extractPage(partiallyFilteredCollection, currentPage, size);
    }

    static newConfigOfType(type): I.Map<string, any> {
        if (!DashboardItemService.itemTypes.includes(type)) {
            throw `Cannot create new config of type '${type}'`;
        }

        return DashboardItemService.defaultConfig.merge(
            I.Map({
                itemType: type,
            }),
        );
    }

    static getUpdatedConfigForConform(
        previousConfig: VisConfig,
        nextConfig: Partial<VisConfig>,
    ): I.Map<string, any> {
        return I.fromJS(_.assign({}, previousConfig, nextConfig));
    }

    /**
     *
     * @param config An ImmutableJS Map
     * @param {moment.Moment} timelineStartMoment Momemt
     * @param {moment.Moment} timelineEndMoment Moment
     * @returns {(any)[]}
     */
    static calculateStartEndMoments(
        config,
        timelineStartMoment: Moment,
        timelineEndMoment: Moment,
    ): [Moment, Moment] {
        const startTime = config.get('startTime');
        const endTime = config.get('endTime');

        let startMoment, endMoment;

        if (endTime === EndTimesSetting.CUSTOM) {
            const endTimestamp = config.get('endTimestamp');
            endMoment = endTimestamp ? moment(endTimestamp) : null;
        } else if (endTime === EndTimesSetting.SYNC) {
            endMoment = timelineEndMoment;
        } else if (endTime === EndTimesSetting.RELATIVE) {
            endMoment = timelineEndMoment
                .clone()
                .subtract(config.get('endTimeRelativeAmount'), 'minutes');
        } else if (endTime === EndTimesSetting.RELATIVE_CUSTOM) {
            endMoment = timelineEndMoment
                .clone()
                .subtract(config.get('endTimeRelativeCustomAmount'), 'minutes');
        }

        if (startTime === StartTimesSetting.CUSTOM) {
            const startTimestamp = config.get('startTimestamp');
            startMoment = startTimestamp ? moment(startTimestamp) : null;
        } else if (startTime === StartTimesSetting.SYNC) {
            startMoment = timelineStartMoment;
        } else if (startTime === StartTimesSetting.RELATIVE) {
            if (endMoment) {
                startMoment = endMoment
                    .clone()
                    .subtract(config.get('startTimeRelativeAmount'), 'minutes');
            } else {
                startMoment = null;
            }
        } else if (startTime === StartTimesSetting.RELATIVE_CUSTOM) {
            if (endMoment) {
                startMoment = endMoment
                    .clone()
                    .subtract(config.get('startTimeRelativeCustomAmount'), 'minutes');
            } else {
                startMoment = null;
            }
        }
        return [startMoment, endMoment];
    }

    static timeFrameStr(
        config: ImmutableVisConfig,
        dashboardItemStartMoment: Moment,
        dashboardItemEndMoment: Moment,
        timeZone: TimeZone,
    ): string {
        const useIMSQL = config.get('useIMSQL');
        if (useIMSQL) {
            return '';
        }

        // Convert to the appropriate forced / site / browser time zone
        dashboardItemStartMoment = dashboardItemStartMoment.tz(timeZone.string);
        dashboardItemEndMoment = dashboardItemEndMoment.tz(timeZone.string);

        const GteDate = dashboardItemStartMoment.format('MMM D');
        const GteTime = dashboardItemStartMoment.format('HH:mm');
        const LtDate = dashboardItemEndMoment.format('MMM D');
        const LtTime = dashboardItemEndMoment.format('HH:mm');

        return `○ ${GteDate}, ${GteTime} → ${GteDate !== LtDate ? LtDate + ', ' : ''}${LtTime}`;
    }

    static headerStr(
        config: ImmutableVisConfig,
        dashboardItemStartMoment: Moment,
        dashboardItemEndMoment: Moment,
        timeZone: TimeZone,
    ): string {
        const D = DashboardItemService;

        const itemType = config.get('itemType');
        const useGen = config.get('useGeneratedHeader');

        const group = config.get('group');
        const dataField = config.get('dataField');
        const dataSource = config.get('dataSource');

        const groupName = D.groupDetails.getIn([group, 'name']);
        const fieldName = D.fieldDetails.getIn([dataField, 'name']);
        const fieldType = D.fieldDetails.getIn([dataField, 'type']);
        const dataSourceName = D.dataSourceDetails.getIn([dataSource, 'name']);

        const aggregationType = config.get('aggregationType');
        const aggSuffix = itemType === ITEM_TYPE_TABLE ? '' : getAggregationSuffix(aggregationType);

        const groupStr = groupName ? `by ${groupName} ` : '';
        const timeFrameStr = D.timeFrameStr(
            config,
            dashboardItemStartMoment,
            dashboardItemEndMoment,
            timeZone,
        );

        if (useGen && itemType === ITEM_TYPE_CHART) {
            const fieldTypeName = D.fieldTypeDetails.getIn([fieldType, 'name']);

            return `${fieldTypeName}${aggSuffix} ${groupStr}${timeFrameStr}`.trim();
        } else if (useGen && itemType === ITEM_TYPE_TABLE) {
            return `${dataSourceName} ${timeFrameStr}`.trim();
        } else if (useGen && itemType === ITEM_TYPE_STATUS) {
            if (dataSource === group) {
                return `${dataSourceName} ${fieldName}${aggSuffix} ${timeFrameStr}`.trim();
            } else {
                return `${dataSourceName} ${fieldName}${aggSuffix} ${groupStr}${timeFrameStr}`.trim();
            }
        } else if (!useGen) {
            return config.get('userHeader') + ' ' + `${timeFrameStr}`.trim();
        }
    }

    static itemTypeOption(itemType) {
        return I.Map({
            label: DashboardItemService.itemTypeDetails.getIn([itemType, 'name']),
            value: itemType,
        });
    }

    static ignoredStatsOption(value) {
        return I.Map({
            value,
            label: DashboardItemService.ignoredStatsOptionNames.get(value),
        });
    }

    static labelDirectionsOption(value) {
        return I.Map({
            value,
            label: DashboardItemService.labelDirectionsOptionNames.get(value),
        });
    }

    static isActiveOption(value) {
        return I.Map({
            value,
            label: DashboardItemService.isActiveOptionsNames.get(value),
        });
    }

    static autoRefreshRateSecondsOption(value) {
        return I.Map({
            value,
            label: DashboardItemService.autoRefreshRateSecondsOptionsNames.get(`${value}`),
        });
    }

    static statusItemSizeOption(value) {
        return I.Map({
            value,
            label: DashboardItemService.statusItemSizeNames.get(value),
        });
    }

    static chartDisplayTypeOption(value) {
        return I.Map({
            value,
            label: DashboardItemService.chartDisplayTypeNames.get(value),
        });
    }

    static dataFieldOption(dataSource: string, field: string): I.Map<string, unknown> {
        const type = DashboardItemService.fieldDetails.getIn([field, 'type']);

        return I.Map({
            value: field,
            name: DashboardItemService.fieldDetails.getIn([field, 'name']),
            typeName: DashboardItemService.fieldTypeDetails.getIn([type, 'name']),
            type: type,
            source: dataSource,
            sourceName: DashboardItemService.dataSourceDetails.getIn([dataSource, 'name']),
        });
    }

    static validRelativeTimeFramesForConfig(config) {
        const D = DashboardItemService;
        const dataSource = config.get('dataSource');
        const itemType = config.get('itemType');

        const dataSourceTimeFrames = D.dataSourceDetails
            .getIn([dataSource, 'validRelativeTimeFrames'])
            .toOrderedSet();

        const itemTypeTimeFrames = D.itemTypeDetails
            .getIn([itemType, 'validRelativeTimeFrames'])
            .toOrderedSet();

        return dataSourceTimeFrames.intersect(itemTypeTimeFrames).toList();
    }

    static startTimeOptions(config) {
        return NON_RELATIVE_PRESET_START_TIMES.map((value) =>
            I.Map({
                value: `${NON_RELATIVE_PRESET}/${value}`,
                label: NON_RELATIVE_PRESET_START_TIME_NAMES.get(value),
            }),
        ).concat(
            DashboardItemService.validRelativeTimeFramesForConfig(config).map((value) =>
                I.Map({
                    value: `${RELATIVE_PRESET}/${value}`,
                    label: RELATIVE_START_TIME_NAMES.get(value),
                }),
            ),
        );
    }

    static parseChosenTimeOption(option, relativePresetType: string): [string, string] | [string] {
        const [type, value] = (option || `${NON_RELATIVE_PRESET}/${StartTimesSetting.SYNC}}`).split(
            '/',
        );
        if (type === relativePresetType) {
            return [relativePresetType, value];
        } else if (type === NON_RELATIVE_PRESET) {
            return [value];
        }
        throw new Error('Could not parse chosen time option');
    }

    static chosenTimeOption(
        config,
        configPropName: string,
        relativeAmountPropName: string,
        relativePresetType: string,
    ) {
        if (config.get(configPropName) === relativePresetType) {
            return `${RELATIVE_PRESET}/${config.get(relativeAmountPropName)}`;
        } else {
            return `${NON_RELATIVE_PRESET}/${config.get(configPropName)}`;
        }
    }

    static chosenStartTimeOption(config) {
        return this.chosenTimeOption(
            config,
            'startTime',
            'startTimeRelativeAmount',
            StartTimesSetting.RELATIVE,
        );
    }

    static parseChosenStartTimeOption(option) {
        return this.parseChosenTimeOption(option, StartTimesSetting.RELATIVE);
    }

    static chosenEndTimeOption(config) {
        return this.chosenTimeOption(
            config,
            'endTime',
            'endTimeRelativeAmount',
            EndTimesSetting.RELATIVE,
        );
    }

    static parseChosenEndTimeOption(option) {
        return this.parseChosenTimeOption(option, EndTimesSetting.RELATIVE);
    }

    static endTimeOptions(config) {
        return NON_RELATIVE_PRESET_END_TIMES.map((value) =>
            I.Map({
                value: `${NON_RELATIVE_PRESET}/${value}`,
                label: NON_RELATIVE_PRESET_END_TIME_NAMES.get(value),
            }),
        ).concat(
            DashboardItemService.validRelativeTimeFramesForConfig(config).map((value) =>
                I.Map({
                    value: `${RELATIVE_PRESET}/${value}`,
                    label: RELATIVE_END_TIME_NAMES.get(value),
                }),
            ),
        );
    }

    getTimeSliceOptions(
        visConfig: VisConfig,
        clockHandler: ClockHandler,
        configService: ConfigService,
    ) {
        const config = DashboardItemService.conformConfig(
            I.fromJS(visConfig),
            clockHandler,
            configService,
        );
        const [startMoment, endMoment] = DashboardItemService.calculateStartEndMoments(
            config,
            clockHandler.getStartTimeAsMoment(),
            clockHandler.getEndTimeAsMoment(),
        );
        const timeFrameInSeconds =
            startMoment && endMoment ? moment.duration(endMoment.diff(startMoment)).asSeconds() : 0;
        const options = customTimeSliceSizeOptionsForConfig(
            I.fromJS(visConfig),
            timeFrameInSeconds,
            configService,
        );

        return options.toJS();
    }

    getFieldsForDataSource(dataSource: string) {
        return DashboardItemService.dataSourceDetails
            .getIn([dataSource, 'fields'], I.List())
            .toJS();
    }

    getDataFieldName(dataField: string): string {
        return DashboardItemService.fieldDetails.getIn([dataField, 'name']);
    }

    getDataSourceName(dataSource: string): string {
        return DashboardItemService.dataSourceDetails.getIn([dataSource, 'name']);
    }

    getAggregationsForField(dataField: string): AggregationType[] {
        return DashboardItemService.fieldDetails.getIn([dataField, 'aggregationTypes']);
    }

    static dataFieldOptionsByItemType(itemType: string) {
        const D = DashboardItemService;
        const allOptions = D.dataSources.reduce((options, dataSource) => {
            return options.concat(
                D.dataSourceDetails
                    .getIn([dataSource, 'fields'])
                    .map((field) => {
                        return D.dataFieldOption(dataSource, field);
                    })
                    .toJS(),
            );
        }, []);
        if (itemType === ITEM_TYPE_TABLE) {
            return _.uniqBy(allOptions, 'sourceName');
        } else if (itemType === ITEM_TYPE_CHART) {
            return _.uniqBy(allOptions, 'type');
        } else {
            return allOptions;
        }
    }

    static dataFieldOptionsForConfig(config) {
        const D = DashboardItemService;
        const itemType = config.get('itemType');
        return D.dataFieldOptionsByItemType(itemType);
    }

    static aggregationTypeOptionsForConfig(
        config: ImmutableVisConfig,
        itemType: string,
    ): I.List<Map<string, string>> {
        const D = DashboardItemService;
        const aggregationTypeOptions = D.aggregationTypeOptions;
        let availableAggregationTypes;
        if (itemType === ITEM_TYPE_TABLE) {
            /* This is a workaround since the default behaviour is to select the aggregates from the default field.
            This will handle the case for table and check all fields in the data source. */
            const dataSource = config.get('dataSource');
            const allDataSourceDetails = DATA_SOURCE_DETAILS.toJS();
            const dataSourceDetails = allDataSourceDetails[dataSource];
            const aggregationTypesSet = new Set();
            for (const i in dataSourceDetails.fields) {
                const field = dataSourceDetails.fields[i];
                const fieldAggregationTypes = D.fieldDetails.getIn([field, 'aggregationTypes']);
                fieldAggregationTypes.forEach((aggType) => aggregationTypesSet.add(aggType));
            }
            availableAggregationTypes = I.List(aggregationTypesSet);
        } else {
            /* Follow default behaviour and return aggregate types for only the selected dataField. */
            const dataField = config.get('dataField');
            availableAggregationTypes = D.fieldDetails.getIn(
                [dataField, 'aggregationTypes'],
                I.List(),
            );
        }
        if (availableAggregationTypes) {
            return I.List(
                availableAggregationTypes.map((value) => {
                    return aggregationTypeOptions.get(value);
                }),
            );
        } else {
            return I.List();
        }
    }

    static groupOptionsForConfig(config: ImmutableVisConfig) {
        const D = DashboardItemService;
        const dataSource = config.get('dataSource');
        const groups = D.dataSourceDetails.getIn([dataSource, 'groups']);
        const itemType = config.get('itemType');
        const aggregationType = config.get('aggregationType');

        // If it is a table, multiple aggregations could be used on different columns, so don't show it
        const aggSuffix = itemType === ITEM_TYPE_TABLE ? '' : getAggregationSuffix(aggregationType);

        const availableGroups = DashboardItemService.getAvailableGroupOptionsForTimeSlice(
            groups,
            config.get('customTimeSliceSize'),
            itemType === ITEM_TYPE_CHART ? config.get('chartDisplayType') : '',
        );

        return availableGroups.map((value: string) =>
            I.Map({
                value: D.groupDetails.getIn([value, 'value']),
                label: D.groupDetails.getIn([value, 'name']) + aggSuffix,
            }),
        );
    }

    static getAvailableGroupOptionsForTimeSlice(
        groups: any,
        timeSliceSize?: number,
        dashboardChartType?: string,
    ) {
        const excludedGroups = [] as string[];

        if (!ConfigService.enableChartFilterByWifiOptions) {
            excludedGroups.push(GROUP_BY_RADIO_TECHNOLOGY_CATEGORY);
            excludedGroups.push(GROUP_BY_RADIO_TECHNOLOGY_TYPE);
            excludedGroups.push(GROUP_BY_WIFI_CHANNEL);
        } else if (
            (timeSliceSize && timeSliceSize >= WIFI_CHANNEL_TIMESLICE_THRESHOLD) ||
            ![CHART_DISPLAY_TYPE_LINE, CHART_DISPLAY_TYPE_BAR].includes(dashboardChartType ?? '')
        ) {
            //Group by wifi channel only available for trend line/bar dashboard charts where timeslice < threshold
            excludedGroups.push(GROUP_BY_WIFI_CHANNEL);
        }

        return groups.filter((value: string) => !excludedGroups.includes(value));
    }

    static getResourceDefinitionForDataSource(dataSource: string): DataSourceResources {
        return DashboardItemService.dataSourceDetails.getIn([dataSource, 'resourceNames']).toJS();
    }

    static generateOptions(
        config: ImmutableVisConfig,
        timeFrameInSeconds: number,
        configService: ConfigService,
    ) {
        const D = DashboardItemService;
        const itemType = config.get('itemType');
        return I.Map({
            dataField: D.dataFieldOptionsForConfig(config),
            startTime: D.startTimeOptions(config),
            endTime: D.endTimeOptions(config),
            customTimeSliceSize: customTimeSliceSizeOptionsForConfig(
                config,
                timeFrameInSeconds,
                configService,
            ),
            group: D.groupOptionsForConfig(config),
            aggregationTypes: D.aggregationTypeOptionsForConfig(config, itemType),
            itemType: D.itemTypes.map(D.itemTypeOption),
            ignoredStats: D.ignoredStatsOptions.map(D.ignoredStatsOption),
            labelDirections: D.labelDirectionsOptions.map(D.labelDirectionsOption),
            isActive: D.isActiveOptions.map(D.isActiveOption),
            statusItemSize: D.statusItemSizes.map(D.statusItemSizeOption),
            chartDisplayType: D.chartDisplayTypes.map(D.chartDisplayTypeOption),
            autoRefreshRateSeconds: D.autoRefreshRateSecondsOptions.map(
                D.autoRefreshRateSecondsOption,
            ),
        });
    }

    static generateChosenOptions(config) {
        const D = DashboardItemService;

        return I.Map({
            dataField: D.dataFieldOption(config.get('dataSource'), config.get('dataField')),

            startTime: D.chosenStartTimeOption(config),

            startTimestamp: config.get('startTimestamp'),
            endTime: D.chosenEndTimeOption(config),
            endTimestamp: config.get('endTimestamp'),

            startTimeRelativeCustomAmount: config.get('startTimeRelativeCustomAmount'),
            endTimeRelativeCustomAmount: config.get('endTimeRelativeCustomAmount'),

            customTimeSliceSize: config.get('customTimeSliceSize'),
            useAutoTimeSliceSize: config.get('useAutoTimeSliceSize'),
            useGeneratedHeader: config.get('useGeneratedHeader'),
            userHeader: config.get('userHeader'),
            filterUsingQuicklist: config.get('filterUsingQuicklist'),
            includeNullSeries: config.get('includeNullSeries'),
            itemType: config.get('itemType'),
            ignoredStats: config.get('ignoredStats'),
            labelDirections: config.get('labelDirections'),
            showAllLabels: config.get('showAllLabels'),
            isActive: config.get('isActive'),
            group: config.get('group'),
            useSubgroup: config.get('useSubgroup'),
            includePartialTimeSlice: config.get('includePartialTimeSlice'),
            statusItemSize: config.get('statusItemSize'),
            chartDisplayType: config.get('chartDisplayType'),
            aggregationType: config.get('aggregationType'),

            autoRefreshRateSeconds: config.get('autoRefreshRateSeconds'),
            useIMSQL: config.get('useIMSQL'),
            imsql: config.get('imsql'),
            yAxisValues: config.get('yAxisValues'),
            hiddenColumns: config.get('hiddenColumns'),
        });
    }

    static conformConfig(
        config: ImmutableVisConfig,
        clockHandler: ClockHandler,
        configService: ConfigService,
    ) {
        const D = DashboardItemService;

        const defaultedConfig: ImmutableVisConfig = D.defaultConfig.merge(
            config.filter((v) => v !== undefined),
        );
        const dataSource = defaultedConfig.get('dataSource');

        const oldStartTime = defaultedConfig.get('startTime');
        const oldEndTime = defaultedConfig.get('endTime');
        const oldStartTimeRelativeAmount = parseInt(config.get('startTimeRelativeAmount')) || null;
        const oldEndTimeRelativeAmount = parseInt(config.get('endTimeRelativeAmount')) || null;

        const getTimeRelativeAmount = (
            oldTime: StartTimesSetting,
            oldTimeRelativeAmount: number,
        ) => {
            if (oldTime === StartTimesSetting.RELATIVE) {
                const validRelativeAmounts = D.validRelativeTimeFramesForConfig(config);
                if (
                    oldTimeRelativeAmount &&
                    !validRelativeAmounts.includes(oldTimeRelativeAmount)
                ) {
                    return validRelativeAmounts.first();
                }
            }
            return oldTimeRelativeAmount;
        };

        const groups = DashboardItemService.getAvailableGroupOptionsForTimeSlice(
            D.dataSourceDetails.getIn([dataSource, 'groups']),
            config.get('customTimeSliceSize'),
            config.get('itemType') === ITEM_TYPE_CHART ? config.get('chartDisplayType') : '',
        );

        const oldGroup = defaultedConfig.get('group');
        const group =
            oldGroup === 'all' ? null : groups.includes(oldGroup) ? oldGroup : groups.first();

        const itemType = config.get('itemType');
        const aggregationTypes = D.aggregationTypeOptionsForConfig(config, itemType).map(
            (aggType) => aggType.get('value'),
        );

        const oldAggregationType = defaultedConfig.get('aggregationType');
        const aggregationType = aggregationTypes.includes(oldAggregationType)
            ? oldAggregationType
            : aggregationTypes.first();

        const supportedFilters = D.dataSourceDetails.getIn([dataSource, 'filters'], I.fromJS({}));

        const oldIsActive = defaultedConfig.get('isActive');
        const isActive = supportedFilters.get('isActive') ? oldIsActive : undefined;

        const oldIgnoredStats = defaultedConfig.get('ignoredStats');
        const ignoredStats = supportedFilters.get('ignoredStats') ? oldIgnoredStats : undefined;

        const labelDirections = defaultedConfig.get('labelDirections');
        const showAllLabels = defaultedConfig.get('showAllLabels');

        const useIMSQL = defaultedConfig.get('useIMSQL');
        const includeNullSeries = defaultedConfig.get('includeNullSeries');

        // if we're NOT using IMSQL, set auto refresh rate to nothing, otherwise if we are using IMSQL, use whatever was set to.. or if it wasn't set, use a sensible default
        const prevAutoRefreshRate = defaultedConfig.get('autoRefreshRateSeconds');
        const autoRefreshRateSeconds = useIMSQL
            ? _.isNil(prevAutoRefreshRate)
                ? 60
                : prevAutoRefreshRate
            : null;

        // if using imsql, we can't generate an automatic header, so force it to false
        const useGeneratedHeader = useIMSQL ? false : defaultedConfig.get('useGeneratedHeader');

        let availableFilterTypes = D.dataSourceDetails.getIn([dataSource, 'filterTypes'], I.List());
        const hasSubgroup = D.dataSourceDetails.getIn([dataSource, 'hasSubgroup']);

        if (hasSubgroup) {
            availableFilterTypes = availableFilterTypes.push('subgroup_name');
        }

        const currentFilterSet = defaultedConfig.get('filterSet', I.Map());
        // filter set is sometimes a JS object, sometimes an immutable object.. I'm not sure
        const currentNormalizedFilterSet = !currentFilterSet
            ? I.Map()
            : currentFilterSet instanceof I.Map
              ? currentFilterSet
              : I.fromJS(currentFilterSet);
        const filteredFilterSet = currentNormalizedFilterSet.filter((value, key) =>
            availableFilterTypes.includes(key),
        );

        const initialConformedConfig = defaultedConfig
            .set(
                'startTimeRelativeAmount',
                getTimeRelativeAmount(oldStartTime, oldStartTimeRelativeAmount),
            )
            .set(
                'endTimeRelativeAmount',
                getTimeRelativeAmount(oldEndTime, oldEndTimeRelativeAmount),
            )
            .set('group', group)
            .set('filterSet', filteredFilterSet)
            .set('includeNullSeries', includeNullSeries)
            .set('aggregationType', aggregationType)
            .set('isActive', isActive)
            .set('ignoredStats', ignoredStats)
            .set('labelDirections', labelDirections)
            .set('showAllLabels', showAllLabels)
            .set('useGeneratedHeader', useGeneratedHeader)
            .set('autoRefreshRateSeconds', autoRefreshRateSeconds)
            .set('useIMSQL', useIMSQL)
            .set('imsql', defaultedConfig.get('imsql'))
            .set('postAggregationFilters', defaultedConfig.get('postAggregationFilters'))
            .set('yAxisValues', defaultedConfig.get('yAxisValues'))
            .set('hiddenColumns', defaultedConfig.get('hiddenColumns'));

        // calculate our time range from the partially conformed config.
        //  this is not so nice, and we should definitely break out the parameters from the config that we need
        //  from this method call, rather than passing in a half-baked config.
        const [startMoment, endMoment] = DashboardItemService.calculateStartEndMoments(
            initialConformedConfig,
            clockHandler.getStartTimeAsMoment(),
            clockHandler.getEndTimeAsMoment(),
        );

        // calculate the time range in our config if we can
        const hasTimeRange = startMoment && endMoment;
        const timeFrameInSeconds = hasTimeRange
            ? moment.duration(endMoment.diff(startMoment)).asSeconds()
            : null;

        const isUsingAutoTimeSliceSize = defaultedConfig.get('useAutoTimeSliceSize', false);
        const defaultAutoTimeSliceSize = autoTimeSliceSize(
            dataSource,
            timeFrameInSeconds,
            configService,
        );

        // conform our time slice settings
        const oldCustomTimeSliceSize = defaultedConfig.get('customTimeSliceSize');
        const validCustomTimeSliceSizes = hasTimeRange
            ? validTimeSliceSizes(dataSource, timeFrameInSeconds, configService)
            : null;

        // if we're not using auto time slice sizing, set custom time slice to null, to keep it clean
        //  otherwise, if we have a time range and our existing custom time size is still valid, keep it
        //  otherwise, use a default custom time slice, which the current auto value
        const customTimeSliceSize = !isUsingAutoTimeSliceSize
            ? hasTimeRange && validCustomTimeSliceSizes.includes(oldCustomTimeSliceSize)
                ? oldCustomTimeSliceSize
                : defaultAutoTimeSliceSize
            : null;

        return initialConformedConfig.set('customTimeSliceSize', customTimeSliceSize);
    }

    static drillDownIntoGroup(config, groupId) {
        const D = DashboardItemService;

        const dataSource = config.get('dataSource');
        const defaultGroup = D.dataSourceDetails.getIn([dataSource, 'defaultGroup']);

        const parentType = D.dataSourceDetails.getIn([dataSource, 'parentType']);
        const hasSubgroup = D.dataSourceDetails.getIn([dataSource, 'hasSubgroup']);
        const currentGroupBy = config.get('group');
        const currentFilterSet = config.get('filterSet');

        const newFilterType = D.groupDetails.getIn([currentGroupBy, 'filterType']);
        let newGroup = D.groupDetails.getIn([currentGroupBy, 'drillDownGroup'], defaultGroup);
        let selectedSubgroup = null;
        let newFilterSet = currentFilterSet;

        // when the parent type is 'asset', grouping by ip device is an invalid option and only one option is valid
        if (parentType == STAT_DEFINITION__PARENT_TYPE_ASSET) {
            newGroup = 'asset';
        }

        if (hasSubgroup) {
            // extract the selected subgroup and group id
            selectedSubgroup = `${groupId}`.split('_')[1];
            groupId = parseInt(`${groupId}`.split('_')[0]);

            if (selectedSubgroup) {
                newFilterSet = FilterService.addItemToSet(
                    newFilterSet,
                    'subgroup_name',
                    selectedSubgroup,
                );
            }
        }

        newFilterSet = currentGroupBy
            ? FilterService.addItemToSet(newFilterSet, newFilterType, groupId)
            : newFilterSet;

        return config.merge(
            I.Map({
                filterSet: newFilterSet,
                group: newGroup,
            }),
        );
    }

    /**
     * Because legend state is never saved to the API,
     * when a dashboard item visConfig is updated from the API it can get
     * us into a funny state when combined with the local config history
     * that includes legend state.
     * This function exists to help reset the legend state to something that
     * makes sense.
     * @returns {LegendState}
     * @param dashboardItemStateProvider
     */
    static getDefaultLegendState(
        dashboardItemStateProvider: DashboardItemStateProvider,
    ): LegendState {
        const itemType: string = dashboardItemStateProvider.visConfig.itemType;
        if (
            !forceDisablePagination &&
            itemType === ITEM_TYPE_CHART &&
            PAGINATION_CHARTS.includes(dashboardItemStateProvider.visConfig.chartDisplayType)
        ) {
            // Only charts have default pagination
            return {
                currentPage: 1,
                pageSize: 50,
                groupIdListToHide: [],
            };
        } else {
            // Of note is pageSize null which disables pages
            return {
                currentPage: 1,
                pageSize: null,
                groupIdListToHide: [],
            };
        }
    }

    /**
     * Answers the question: Can the data set be filtered by the given filterSet ?
     *
     * An example of the purpose of this function is where the dataSource is ip_device, i.e.
     * ip_device_stats but the user selects a zone on the map. ip_device_stats cannot be filtered
     * by a zone so we actually want no filter to be applied to get the 'null' aggregation, i.e.
     * everything is aggregated into a single group.
     *
     * @param {ImmutableFilterSet} filterSet
     * @param {string} dataSource
     * @returns {boolean}
     */
    static canFilterDataSourceByFilterSet(
        filterSet: ImmutableFilterSet,
        dataSource: string,
    ): boolean {
        let result = false;
        const dataSourceFilterTypes = DATA_SOURCE_DETAILS.get(dataSource).get('filterTypes');
        filterSet.forEach((filterValue, filterKey: string) => {
            if (dataSourceFilterTypes.contains(filterKey)) {
                result = true;
            }
        });
        return result;
    }

    registerConfig(id: string, config: I.Map<string, any>) {
        if (_.has(this.histories, id)) {
            log(`Registered config for id '${id}' already exists!`);
        } else {
            _.set(this.histories, id, {
                index: 0,
                history: [config],
            });
            log(`Registering config for id '${id}'`, config);
        }
    }

    /**
     * Callback fires when any change is made to an event history.
     * It's inefficient but it should be ok as not much work
     * is done in the callback at the moment.
     * @param callback
     * @returns {() => any}
     */
    onConfigHistoryUpdate(callback) {
        return this._historyUpdatedEvent.addEventListener(callback);
    }

    /**
     * Fires an event when the dashboard item service is ready, including all dynamic stats
     * @param callback
     * @returns {() => any}
     */
    onDashboardItemServiceChange(callback) {
        return this._dashboardItemServiceChangeEvent.addEventListener(callback);
    }

    /**
     * Used to forget all of the existing history state and place it
     * with a new config object
     * @param id
     * @param config
     */
    resetConfigHistory(id: string, config: I.Map<string, any>) {
        delete this.histories[id];
        this.registerConfig(id, config);
        this._historyUpdatedEvent.notifyListeners();
    }

    addConfigChangeToHistory(id, config: I.Map<string, any>) {
        const historyObj = _.get(this.histories, id);
        if (historyObj) {
            const { index, history } = _.get(this.histories, id);

            if (!I.is(config, _.get(history, index))) {
                const newHistory = history.slice(0, index + 1);
                newHistory.push(config);

                _.set(this.histories, [id, 'history'], newHistory);
                _.set(this.histories, [id, 'index'], index + 1);
            }
        }
        this._historyUpdatedEvent.notifyListeners();
        return config;
    }

    undoLastChangeToConfig(id: string) {
        const { index, history } = _.get(this.histories, id);

        if (index === 0) {
            throw 'Cannot undo config change, already at earliest';
        }

        _.set(this.histories, [id, 'index'], index - 1);
        this._historyUpdatedEvent.notifyListeners();
        return _.get(history, index - 1);
    }

    redoLastChangeToConfig(id: string) {
        const { index, history } = _.get(this.histories, id);

        if (index === history.length - 1) {
            throw 'Cannot redo config change, already at latest';
        }

        _.set(this.histories, [id, 'index'], index + 1);
        this._historyUpdatedEvent.notifyListeners();
        return _.get(history, index + 1);
    }

    /**
     * Adds the visConfig + legendState to the history in a single item.
     * Normally used during initialisation
     * @param {DashboardItemStateProvider} dashboardItemStateProvider
     * @param {ClockHandler} clockHandler
     */
    addDashboardItemChangeToHistory(
        dashboardItemStateProvider: DashboardItemStateProvider,
        clockHandler: ClockHandler,
    ) {
        const historyItem = this.createHistoryItem(dashboardItemStateProvider, clockHandler);
        this.addConfigChangeToHistory(dashboardItemStateProvider.uuid, historyItem);
        return historyItem;
    }

    /**
     * Wraps up the immutable map history saving to work with
     * the DashboardItemStateProvider
     * @param {DashboardItemStateProvider} dashboardItemStateProvider
     * @param {ClockHandler} clockHandler
     * @returns {Map<string, any>}
     */
    resetDashboardItemChangeHistory(
        dashboardItemStateProvider: DashboardItemStateProvider,
        clockHandler: ClockHandler,
    ) {
        const historyItem = this.createHistoryItem(dashboardItemStateProvider, clockHandler);
        this.resetConfigHistory(dashboardItemStateProvider.uuid, historyItem);
        return historyItem;
    }

    /**
     * * Wraps up the immutable map history saving to work with
     * the DashboardItemStateProvider
     * @param {DashboardItemStateProvider} dashboardItemStateProvider
     */
    undoLastChangeToDashboardItemStateProvider(
        dashboardItemStateProvider: DashboardItemStateProvider,
    ) {
        const historyObj = this.undoLastChangeToConfig(dashboardItemStateProvider.uuid).toJS();
        this._applyHistoryObjToDashboardItemStateProvider(historyObj, dashboardItemStateProvider);
    }

    /**
     * * Wraps up the immutable map history saving to work with
     * the DashboardItemStateProvider
     * @param {DashboardItemStateProvider} dashboardItemStateProvider
     */
    redoLastChangeToDashboardItemStateProvider(
        dashboardItemStateProvider: DashboardItemStateProvider,
    ) {
        const historyObj = this.redoLastChangeToConfig(dashboardItemStateProvider.uuid).toJS();
        this._applyHistoryObjToDashboardItemStateProvider(historyObj, dashboardItemStateProvider);
    }

    /**
     * Creates an immutable map of the current state to save from a
     * DashboardItemStateProvider. Note that when an item is locked
     * only the legend state is persisted in the history.
     * @param {DashboardItemStateProvider} dashboardItemStateProvider
     * @param {ClockHandler} clockHandler
     * @returns {Map<string, any>}
     */
    createHistoryItem(
        dashboardItemStateProvider: DashboardItemStateProvider,
        clockHandler: ClockHandler,
    ): I.Map<string, any> {
        const result = {};
        if (!dashboardItemStateProvider.locked) {
            const visConfig = I.fromJS(
                Object.assign({}, dashboardItemStateProvider.getDashboardItem().visConfig),
            );
            const conformedConfig: I.Map<string, any> = DashboardItemService.conformConfig(
                visConfig,
                clockHandler,
                this._configService,
            );
            result[VIS_CONFIG] = conformedConfig.toJS();
        }
        // always include legend state
        result[LEGEND_STATE] = dashboardItemStateProvider.getLegendState();
        return I.fromJS(result);
    }

    _applyHistoryObjToDashboardItemStateProvider(
        historyObj: DashboardItemHistory,
        dashboardItemStateProvider: DashboardItemStateProvider,
    ) {
        if (historyObj[LEGEND_STATE]) {
            dashboardItemStateProvider.setLegendState(historyObj[LEGEND_STATE] as LegendState);
        }
        if (historyObj[VIS_CONFIG]) {
            dashboardItemStateProvider.saveVisConfig(historyObj[VIS_CONFIG]);
        }
    }

    configHistoryStatus(id) {
        if (_.has(this.histories, id)) {
            const { index, history } = _.get(this.histories, id);
            return {
                canUndo: index > 0,
                canRedo: index < history.length - 1,
            };
        } else {
            return {
                canUndo: false,
                canRedo: false,
            };
        }
    }

    getConfigHistories() {
        return _.cloneDeep(this.histories);
    }

    /**
     * A most unfortunate function...
     * Tries to do a good job of creating a new legend config that is consistent with a change in vis config
     * and keeps the UI feeling nice.
     * A result of the complexities of visConfig being API persisted while legend being not API persisted.
     * @param oldVisConfig - plain old js object. Same as the visConfig property in a DashboardItem object
     * @param newVisConfig - plain old js object. Same as the visConfig property in a DashboardItem object
     * @returns {LegendState}
     */
    static getLegendStateForVisConfigChange(oldVisConfig, newVisConfig): LegendState {
        const hasGroupChanged = !_.isEqual(oldVisConfig.group, newVisConfig.group);
        const hasFilterChanged = !_.isEqual(oldVisConfig.filterSet, newVisConfig.filterSet);
        const dataSourceChanged = !_.isEqual(oldVisConfig.dataSource, newVisConfig.dataSource);

        const resetHideIdList = hasGroupChanged || hasFilterChanged || dataSourceChanged;

        let disablePagination =
            oldVisConfig.itemType === ITEM_TYPE_CHART && newVisConfig.itemType !== ITEM_TYPE_CHART;
        disablePagination =
            forceDisablePagination ||
            disablePagination ||
            (PAGINATION_CHARTS.includes(oldVisConfig.chartDisplayType) &&
                !PAGINATION_CHARTS.includes(newVisConfig.chartDisplayType));

        let enablePagination =
            oldVisConfig.itemType !== ITEM_TYPE_CHART &&
            newVisConfig.itemType === ITEM_TYPE_CHART &&
            PAGINATION_CHARTS.includes(newVisConfig.chartDisplayType);
        enablePagination =
            enablePagination ||
            (!PAGINATION_CHARTS.includes(oldVisConfig.chartDisplayType) &&
                PAGINATION_CHARTS.includes(newVisConfig.chartDisplayType));

        const legendState: LegendState = {};

        if (disablePagination) {
            legendState.pageSize = null;
            legendState.currentPage = 1;
        } else if (enablePagination) {
            legendState.pageSize = DashboardItemService.maxTrendDatasetSize;
            legendState.currentPage = 1;
        }

        if (resetHideIdList) {
            legendState.groupIdListToHide = [];
        }

        return legendState;
    }
}

interface DashboardItemHistory {
    legendState?: any;
    visConfig?: any;
}
